// -----------------------------------------------------------------------------
// FollowUpV2 — Follow-Up V2 contact modal (checklist-guided)
//
// Self-contained fullscreen modal using the Attended-family chrome.
// Shown on Follow-Up V2 practices (is_followup_v2_enabled).
// Parallel to legacy FollowUp; the legacy modal is untouched.
//
// Callsites: OverView.jsx, LeadDetailTasks.jsx — both gated on
//   useIsFollowUpV2Practice().
//
// Auto-complete mechanic (Raj decision 2026-06-06, Task #62):
//   Log WhatsApp / Phone / Next Action → dispatch completeChecklistItem()
//   IMMEDIATELY on click. Each card action persists to the BE on click,
//   mirroring Attended's handlePanelComplete. No queue, no batch commit on
//   Finish. Finish just closes the modal + reloads the leads table.
//
// Sidebar tabs mirror OpenConsultationPopup.jsx exactly:
//   Overview (grommet-icons:overview) → OverviewTab
//   Notes (solar:chat-round-dots-bold) → NotesTab
//   Calls (solar:phone-calling-rounded-bold) → CallLogs
//   Email (mdi:email-outline) → IosEmailComposerBody compact
//   WhatsApp (ic:baseline-whatsapp) → FollowUpWhatsappChat
//   Debug (solar:bug-bold-duotone) → inline monospace box — superadmin only
//
// Body cards mirror AttendedGuidedChecklistV2 exactly:
//   Hero card above list → UpNextCardV2 (first OPEN item)
//   Smaller cards row → ChecklistMiniGrid
//   onMarkComplete → completeChecklistItem (immediate POST, same as Attended).
//   onUndo → reopenChecklistItem (immediate POST).
//   V2 now matches Attended's immediate-persist behavior exactly (Task #62, 2026-06-06).
//
// Promoted from src/scratch/followupV2/FollowUpV2OptionShell.jsx — Sprint 1.
// Scratch copy retained for Sprints 2/3 reference; do not delete.
// -----------------------------------------------------------------------------
/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { Box, Stack, Typography, Chip, Button, Select, MenuItem, Tooltip, Zoom } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { pillBadge } from 'src/styles/LeadDetailView';
import { getLeadStatusUi } from 'src/utils/leadStatusUi';
import Iconify from 'src/components/Iconify';
import ScheduleTaskReminder from 'src/components/Attended/ScheduleTaskReminder';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import useAuth from 'src/hooks/useAuth';
import OverviewTab from 'src/sections/@dashboard/lead/OverviewTab';
import NotesTab from 'src/sections/@dashboard/lead/NotesTab';
import CallLogs from 'src/sections/@dashboard/leadDetail/CallLogs';
import IosEmailComposerBody from 'src/sections/tasks/composers/IosEmailComposerBody';
import FollowUpWhatsappChat from 'src/components/whatsappComponents/FollowUpWhatsappChat';
import { resolveWhatsappSidebarTemplate } from 'src/components/whatsappComponents/quickReplyUtils';
import AttendedFullscreenWrapper from 'src/components/fullScreenPopUps/AttendedFullscreenWrapper';
import ContactPopupDebugBox from 'src/components/fullScreenPopUps/ContactPopupDebugBox';
import AttendedPopupShell from 'src/components/Attended/AttendedPopupShell';
import TaskCardV2 from 'src/components/tasks/TaskCardV2';
// #64a: V2-native step 1 replaces V1 TreatmentAndAppointment in the BOOK_APPOINTMENT
// BOOKING_DETAILS step. Other 4 steps (PERSONAL_DETAILS / PAYMENT_OPTIONS /
// DENTALLY_CONFIRMATION / MESSAGE_REMINDER) still use their V1 imports below.
import V2BookingDetailsStep from 'src/sections/@dashboard/leadDetail/FollowUpV2Booking/V2BookingDetailsStep';
// Task #63 follow-up (2026-06-07): post-booking wizard steps — mirror V1's
// OverView.jsx:877-903 step array verbatim (Booking Details → Personal Details →
// Payment options → [optional] Dentally Confirmation → Message review & reminders).
// V1 closes the modal AFTER MessageAndReminder.handleNext fires; V2 was closing on
// TreatmentAndAppointment.handleNext (the FIRST step), which skipped the toast +
// the entire booked-flow UX.
import PersonalDetails from 'src/sections/@dashboard/general/app/PersonalDetails';
import useDentallyIntegrationScope from 'src/hooks/useDentallyIntegrationScope';
import DentallyScopePendingStep from 'src/components/dentally/DentallyScopePendingStep';
import PaymentOption from 'src/sections/@dashboard/general/app/PaymentOption';
import MessageAndReminder from 'src/sections/@dashboard/general/app/MessageAndReminder';
import { PmsAvailabilityWizardStep, PmsBookingConfirmStep } from 'src/components/pms';
// Task #63: Dentally booking flow — mirrors OverView.jsx:80
// DentallyPaymentConfirmation added 2026-06-07 — used in the booked-flow Dentally
// branch when both dentallyEnabledGlobal && isDentallyBooking are true (V1 OverView.jsx:894-899).
import {
    DentallyTreatmentAvailabilityWizardStep,
    DentallyPatientDetailsWizardStep,
    DentallyFollowUpConfirmWizardStep,
    BookingModeToggle,
    DentallyPaymentConfirmation,
} from 'src/components/dentally';
import { Archive } from 'src/components/tabs';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { deriveLeadNextActionFooter } from 'src/utils/leadNextActionFooter';
import capitalizeFirstLetter from 'src/utils/capitalizedFunc';
import resolveLeadTreatmentPrice from 'src/utils/resolveLeadTreatmentPrice';
import { resolveContactLead, resolveContactLeadId, describeContactLeadSources } from 'src/utils/resolveContactLead';
import { callApi } from 'src/utils/apiCall';
import { getChoicesList } from 'src/redux/slices/practiceTreatment';
import AttendedFamilyHero from 'src/components/Attended/AttendedFamilyHero';
import { getTreatmentDate } from 'src/utils/multiTreatmentFlow';
import PmsSchedulingStatus from '../../../components/pms/PmsSchedulingStatus';
import usePmsSchedulingScope from '../../../hooks/usePmsSchedulingScope';
import { getLeadNotes, getLeadReminderData, followUpSlice, tableLead, beginExistingLeadBooking, saveNewLeadDetail, updateCompleteStatus, updateSteps, updateBookingSteps, updateAssignToUser } from '../../../redux/slices/lead';
import { beginArchiveLead, updateLeadDetail, updatePersonalDetail } from '../../../redux/slices/lead/coreThunks';
import { leadTreatmentPayment } from '../../../redux/slices/lead/financialThunks';
import { completeChecklistItem, completeTask } from '../../../redux/slices/taskSlice';
import { createWhatsappTaskAnchor, planWhatsappCompletion } from './whatsappSendCompletion';
import { CHECKLIST_COMPLETION_STATE } from '../../../constants/tasksV2';
import {
    selectLeadFlowReminder,
    selectLeadNotes,
    selectLeadNotesArr,
} from '../../../redux/selectors/leadSelectors';
import { useSelector, dispatch, store } from '../../../redux/store';
import useFeaturePermission from '../../../hooks/useFeaturePermission';
import useLeadReminderTasks from './hooks/useLeadReminderTasks';
import ScheduleReminderTaskSection from './ScheduleReminderTaskSection';
import SimilarLeadWarningPopup from './SimilarLeadWarningPopup';

// ── Preferred-contact display constants — mirrors FollowUp.jsx:69-91 ─────────
const PREFERRED_CONTACT_METHOD = [
    { label: 'give me a call 📞', value: 'PHONE' },
    { label: 'send me a text 📱', value: 'TEXT' },
    { label: 'Whatsapp me 💬', value: 'WHATSAPP' },
    { label: 'drop me an email 📧', value: 'EMAIL' },
    { label: 'contact me', value: '' },
];

const PREFERRED_CONTACT_DAYS = [
    { label: 'Monday', value: 'MONDAY' },
    { label: 'Tuesday', value: 'TUESDAY' },
    { label: 'Wednesday', value: 'WEDNESDAY' },
    { label: 'Thursday', value: 'THURSDAY' },
    { label: 'Friday', value: 'FRIDAY' },
    { label: 'Saturday', value: 'SATURDAY' },
    { label: 'Sunday', value: 'SUNDAY' },
];

const CONTACT_PERIOD = [
    { label: 'Morning (8AM - 12PM)', value: 'MORNING' },
    { label: 'Afternoon (12PM - 4PM)', value: 'AFTERNOON' },
    { label: 'Evening (4PM - 8PM)', value: 'EVENING' },
];

// Shared Select sx for the preferred-contact dropdowns in headerNode
// Mirrors FollowUp.jsx:435-448 (method), 467-480 (days), 499-511 (period)
const CONTACT_SELECT_SX = {
    minWidth: '90px',
    height: '26px',
    borderRadius: '10px',
    background: 'transparent',
    boxShadow: 'none',
    border: '1px solid rgba(145, 158, 171, 0.16)',
    fontSize: '12px',
    fontWeight: 500,
    color: 'text.secondary',
    '&:hover': { borderColor: 'rgba(145, 158, 171, 0.3)' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-select': { padding: '2px 8px' },
};

// Action-type picker tabs — 4 V2 outcomes (Raj decision 2026-06-07).
// Trimmed from 6 → 4: WhatsApp / Phone / Next Action chips removed because each open
// checklist card already exposes its own action button for those item types (clicking
// the hero "Mark complete" or the card's per-type icon dispatches the same
// completeChecklistItem thunk). The picker is now reserved for LEAD-LEVEL outcomes that
// have no per-card equivalent: Book (opens wizard), Archive (closes the lead with a
// reason), Add reminder (creates a new task), Can't Reach (logs the attempt without
// completing a specific card).
//
// Mirrors Attended's outcome model — see FollowUp.jsx:697-705 (V1) and the corresponding
// outcome card group on the Attended popup. The "Add reminder" chip routes to the same
// ScheduleTaskReminder component Attended uses (useReminderContent.jsx:137-154).
// V2_ACTIONS shape:
//   key         — internal identifier (matches checklist item types where applicable)
//   label       — the value that becomes `tab.value` in the shell tabs array;
//                 the shell gates on `tab.value` at AttendedPopupShell:195/203 for the
//                 special Reminder & Follow-up reminderNode swap.
//   displayLabel (optional) — user-facing chip text. Falls through to `label` when
//                 absent. Added 2026-06-07 to let the leftmost TASKS chip display
//                 'Active tasks' while keeping `label === 'Reminder & Follow-up'` so
//                 the shell gate still recognises it. Mirrors the same field added to
//                 Attended's PROFILE_TABS (ConsultationAttendedAppointment.jsx:362).
const V2_ACTIONS = [
    {
        // Leftmost chip — mirrors Attended's PROFILE_TABS leftmost entry
        // (ConsultationAttendedAppointment.jsx:362-380). When active, the shared
        // AttendedPopupShell intercepts at line 195/203 and renders reminderNode
        // (V2's checklist) in the body instead of the OUTCOME box.
        // Default activeTab on modal open (see line ~301 useState) so the chip
        // shows highlighted on entry.
        key: 'TASKS',
        label: 'Reminder & Follow-up',
        displayLabel: 'Active tasks',
        sub: 'V2 task checklist',
        icon: 'solar:bell-bold-duotone',
        color: '#FFAB00',
        bg: 'rgba(255,171,0,0.08)',
    },
    {
        key: 'BOOK_APPOINTMENT',
        // label = internal value (handleTabChange.find lookup key). Keep 'Book' as a
        // stable identifier. displayLabel renames the chip text to 'Book appointment'
        // per Raj 2026-06-07 — falls through `tab.label || tab.value` in
        // AttendedPopupShell at the 4 display sites.
        label: 'Book',
        displayLabel: 'Book appointment',
        sub: 'Opens the booking wizard',
        icon: 'mdi:calendar-clock',
        color: '#00A76F',
        bg: 'rgba(0, 167, 111, 0.08)',
    },
    {
        key: 'ARCHIVE',
        label: 'Archive',
        sub: 'Archive this lead with a reason',
        icon: 'eva:archive-fill',
        color: '#637381',
        bg: 'rgba(99,115,129,0.08)',
    },
    // 'Add reminder' chip removed 2026-06-07 — the Add custom reminder button now
    // lives inside the TASKS tab body (reminderNode) mirroring useReminderContent.jsx:113-159.
    // The underlying LEAD_NEXT_ACTION OutcomePanel branch is left intact (no caller
    // points at it, but kept as defensive dead code in case the chip is reinstated).
    //
    // 'Can't Reach' (UNABLE_TO_CONTACT) chip removed 2026-06-07 per Raj — final chip
    // set is [Active tasks / Book appointment / Archive]. The OutcomePanel branch for
    // UNABLE_TO_CONTACT is left intact (no caller, dead but harmless) in case the chip
    // is reinstated later, same pattern as the dropped LEAD_NEXT_ACTION chip.
];
const V2_TASKS_ACTION = V2_ACTIONS.find((action) => action.key === 'TASKS');
if (import.meta.env.DEV && !V2_TASKS_ACTION) {
    throw new Error('FollowUpV2 requires a TASKS action in V2_ACTIONS.');
}
const V2_TASKS_ACTION_LABEL = V2_TASKS_ACTION?.label || 'Reminder & Follow-up';

// AUTO_COMPLETE_TYPES is still used by useV2ChecklistAutocomplete + handleTabChange to
// detect when an outcome key matches a checklist item type and should open the inline
// ActionPanel. WHATSAPP_SENT and LOG_PHONE_CALL are kept in this set so that if a future
// surface (debug toggle, search, deep link) re-introduces those chips, the auto-complete
// wiring still works. The 2026-06-07 trim only affects V2_ACTIONS (the chip array), not
// the underlying handler dispatch — see comment above V2_ACTIONS.
const AUTO_COMPLETE_TYPES = new Set(['WHATSAPP_SENT', 'LOG_PHONE_CALL', 'LEAD_NEXT_ACTION']);

// Find a treatment's index by id with STRING comparison — leadTreatmentId often
// arrives as a string (row data / URL params) while lead_treatments[].id is numeric.
// Strict === silently missed and fell back to [0], re-targeting the wrong
// duplicate-name treatment. Exported for unit tests.
export const findTreatmentIdxById = (treatments, id) => {
    if (id === null || id === undefined || id === '' || !Array.isArray(treatments)) return -1;
    return treatments.findIndex((lt) => String(lt?.id) === String(id));
};

// Note: sidebar tab routing for ActionPanel (WHATSAPP_SENT → 'whatsapp',
// EMAIL_SENT → 'email', etc.) is handled inside ActionPanel.jsx itself —
// ActionPanel calls onOpenSidebarTab(tab) directly on mount for email types
// and via PANEL_MAP for others. No constant needed here.
// Source: ActionPanel.jsx:165-176.

const makeIconNode = (icon) => <Iconify icon={icon} width={20} height={20} />;


// ── FollowUpV2 ───────────────────────────────────────────────────────────────

// Patient-scoped children and legacy booking thunks share this raw Redux context.
const hasCanonicalPatientContext = (leadDetail, practiceId, followLead, bookingPracticeId) => {
    const patientId = resolveContactLeadId(leadDetail);
    const rawPracticeId = followLead?.practice?.id ?? followLead?.practice;
    return patientId !== null && resolveContactLeadId(followLead) === patientId
        && Array.isArray(followLead?.lead_treatments)
        && practiceId != null && rawPracticeId != null
        && String(rawPracticeId) === String(practiceId)
        && String(bookingPracticeId) === String(practiceId);
};

export default function FollowUpV2({ open, onClose, leadDetail, practiceId, handleReloadLeads, debugSource = 'unknown', leadTreatmentId = null }) {
    // enqueueSnackbar retained for future Sprint 2 toast use
    // eslint-disable-next-line no-unused-vars
    const { enqueueSnackbar } = useCustomSnackbar();
    const methods = useForm({ defaultValues: {} });

    // Superuser gate — mirrors OpenConsultationPopup.jsx:42-43
    const { user: authUser } = useAuth();
    const isSuperuser = !!authUser?.is_superuser;

    // ── Resilience Case 1 + 5: tasks-v2 permission gate ─────────────────────────
    // FollowUpV2 depends on tasks-v2 endpoints (checklist items, complete/, reopen/,
    // pause/, autocomplete queue). Practitioner / TCO roles have tasks-v2 = 'hidden'
    // by default — if they somehow land here (direct URL / deep-link / race on a
    // role check that fires after row-click), the underlying checklist API calls
    // 403 and the body silently breaks. Same applies when the practice has
    // is_followup_v2_enabled=true but the user's role doesn't have tasks-v2.
    // We render a friendly empty state instead (see fallback branch before main return).
    const { isHidden: isTasksV2Hidden } = useFeaturePermission('tasks-v2');

    // ── Debug panel toggle — mirrors AttendedAppointment.jsx:103-112 verbatim ──
    // In dev: on by default; opt-out with ?attDebug=0 or localStorage attDebug='0'.
    // In prod: off by default; opt-in with ?attDebug=1 or localStorage attDebug='1'.
    const attDebug =
        new URLSearchParams(window.location.search).get('attDebug') === '1'
        || (typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '1')
        || (import.meta.env.DEV
            && new URLSearchParams(window.location.search).get('attDebug') !== '0'
            && !(typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '0'));

    // Section-outline helpers — mirrors AttendedAppointment.jsx:130-133.
    // Passed to AttendedPopupShell as dbgSx / DbgTag so the shell's outline
    // calls (line 97: `...dbgSx('#AA00FF')`) and label renders work correctly.
    // When attDebug is false these return null / render nothing — zero overhead.
    // eslint-disable-next-line react/no-unstable-nested-components
    const dbgSx = (color) => (attDebug ? { outline: `2px dashed ${color}`, outlineOffset: '-2px', position: 'relative' } : null);
    // eslint-disable-next-line react/no-unstable-nested-components, react/prop-types
    const DbgTag = ({ color, label }) => (attDebug ? (
        <Box component="span" sx={{ position: 'absolute', top: 0, left: 0, zIndex: 50, bgcolor: color, color: '#fff', fontSize: '9px', fontWeight: 700, lineHeight: 1.4, px: 0.5, borderBottomRightRadius: 4, letterSpacing: '0.04em', pointerEvents: 'none' }}>{label}</Box>
    ) : null);

    // Redux: enriched lead data for sidebar composers — mirrors OpenConsultationPopup.jsx:38-41
    const leadFlowReminder = useSelector(selectLeadFlowReminder);
    const leadNotes = useSelector(selectLeadNotes);
    const leadNotesArr = useSelector(selectLeadNotesArr);
    const followLead = useSelector((state) => state.lead.followLead);
    const storedLeadDetail = useSelector((state) => state.lead.leadDetail);
    const bookingPracticeId = useSelector((state) => state.practiceTreatmentList?.practiceId);
    const canonicalContextReady = hasCanonicalPatientContext(leadDetail, practiceId, followLead, bookingPracticeId)
        && hasCanonicalPatientContext(leadDetail, practiceId, storedLeadDetail, bookingPracticeId);
    // True while a booking POST is in flight (set only by the addAppointment
    // thunk). Drives the Finish spinner and keeps the button from being
    // clicked a second time — the trigger behind the duplicate-appointment
    // defect: the button stayed live for the whole 1-4s request with no
    // feedback, so staff clicked again.
    const bookingInFlight = useSelector((state) => state.lead.bookingInFlight);
    const newLeadDetail = useSelector((state) => state.lead.newLeadDetail);

    // Sidebar state — mirrors OpenConsultationPopup.jsx:28-36.
    // Default tab is 'overview', except when the lead's source is WhatsApp:
    // we open straight into the WhatsApp tab so the user can reply immediately
    // (Raj req 2026-06-08). Source field is `leadDetail.lead_source.type` ===
    // 'WHATSAPP' per LEAD_SOURCE_TYPES (src/constants/LeadSourceConst.js:33).
    const computeInitialSidebarTab = () =>
        (leadDetail?.lead_source?.type === 'WHATSAPP' ? 'whatsapp' : 'overview');
    const [sidebarTab, setSidebarTab] = useState(computeInitialSidebarTab);
    const [sidebarPulse, setSidebarPulse] = useState(0);
    // setSidebarEmailTemplateId called in Sprint 2 when outcomes redirect to email composer
    // eslint-disable-next-line no-unused-vars
    const [sidebarEmailTemplateId, setSidebarEmailTemplateId] = useState(null);
    // WTM (task #89) — mirror of sidebarEmailTemplateId for the WhatsApp tab.
    // Set by handleOpenSidebarTab below when ActionPanel/FollowUpPanel resolves
    // a per-practice WhatsApp template; consumed by FollowUpWhatsappChat below.
    // (Previously carried a stale `eslint-disable no-unused-vars` from scaffolding;
    // both reader and writer are now live, so no disable needed.)
    const [sidebarWhatsappTemplateId, setSidebarWhatsappTemplateId] = useState(null);
    // WTM — the resolved WhatsApp template id + NAME (mapped-template draft path).
    // Set by handleOpenSidebarTab when FollowUpPanel resolves a per-practice
    // WhatsApp mapping; consumed by FollowUpWhatsappChat → ChatPage which matches
    // the template (id-first) and previews + sends it via the approved-template
    // path (works open OR closed window).
    const [sidebarWhatsappTemplateName, setSidebarWhatsappTemplateName] = useState(null);
    const [sidebarWhatsappTemplateWaId, setSidebarWhatsappTemplateWaId] = useState(null);
    // The checklist item id the mapped WhatsApp template came from — scopes the
    // mapped-draft dismissed flag per item.
    const [sidebarWhatsappItemId, setSidebarWhatsappItemId] = useState(null);
    const [notesLength, setNotesLength] = useState([]);
    const [treatmentData, setTreatmentData] = useState(null);

    // Real checklist data + queue API — wired via useLeadReminderTasks (which wraps
    // useV2ChecklistAutocomplete and adds activeTaskIds/activeTaskCount).
    // Queue API dropped — per Raj decision 2026-06-06: mirror Attended's immediate-persist
    // model. Each card action fires a BE call on click; no batch commit on Finish.
    // This is the SINGLE hook instance for this modal — it's also passed down to
    // ScheduleReminderTaskSection via the `checklist` prop so the reminder body and
    // FollowUpV2's own chrome (badge, OutcomePanel) stay in sync off one source of truth.
    const checklist = useLeadReminderTasks({ practiceId, leadId: leadDetail?.id });
    const {
        items, openItemsByType, rawGroups, loading, refresh, activeTaskIds, activeTaskCount,
    } = checklist;

    const [flowStep, setFlowStep] = useState('CONTENT');
    // Default to the TASKS chip on modal open — mirrors Attended where the leftmost
    // 'Reminder & Follow-up' chip is selected by default (the user lands on the
    // checklist immediately). 'Reminder & Follow-up' is the literal value the shell
    // gate at AttendedPopupShell:195/203 looks for to swap the OUTCOME box for the
    // reminderNode. The user-facing chip text is 'Active tasks' (V2_ACTIONS.displayLabel).
    const [activeTab, setActiveTab] = useState(V2_TASKS_ACTION_LABEL);

    // Lead assignees (for hero card avatar stack) — mirrors ConsultationAttendedAppointment.jsx:65
    const [assigneeSelected, setAssigneeSelected] = useState(followLead?.assignees || []);

    // H-2/H-5: PMS booking state — mirrors OverView.jsx:406-410 PMS state
    const pmsScope = usePmsSchedulingScope(practiceId);
    const pmsEnabledGlobal = pmsScope.enabled;
    // Dentally gating — per-practice (useDentallyIntegrationScope), not the
    // app-global `dentallyEnabledGlobal` boolean, which is seeded from
    // localStorage, carries no practice identity and is cleared only by the
    // dashboard header (2026-09-19 gating audit, rows 17-19).
    const dentallyScope = useDentallyIntegrationScope(practiceId);
    const dentallyVisible = dentallyScope.visible;
    const dentallyCanBook = dentallyScope.visible && dentallyScope.canManage;
    const dentallyScopePending = dentallyScope.pending;
    // isDentallyBooking — mirrors OverView.jsx:437 (state.lead). Not actively
    // read inside the V2 BOOK_APPOINTMENT path today, but selected here to keep
    // symmetry with V1's destructure and so future changes pick up the same slice.
    // Used by the V1 attendance branch (OverView.jsx:894) which V2 doesn't host.
    // eslint-disable-next-line no-unused-vars
    const { isDentallyBooking } = useSelector((state) => state.lead);
    // Bug A fix (#93 follow-up 93b): the V2 chain never dispatches
    // followUpAppointmentHandler('Booking Details'), so the selector was
    // returning null and updateLeadDetail bailed inside the addAppointment
    // dispatch chain. We now pass the literal 'Booking Details' string at
    // the call site (line ~1297) — the selector is no longer needed.
    // Referral schema — needed to resolve referral value → label for pillBadge tooltip
    // Mirrors LeadDetailViewBody.jsx:213 — same store slice
    const { referral: referralSchema } = useSelector((state) => state.schema);
    const [pmsSlot, setPmsSlot] = useState(null);
    const [pmsPractitioner, setPmsPractitioner] = useState(null);
    const [pmsDate, setPmsDate] = useState(null);
    const [pmsBookingStep, setPmsBookingStep] = useState('AVAILABILITY'); // 'AVAILABILITY' | 'CONFIRM'
    // Gap #3 (Task #63): PmsBookingConfirmStep step-state pass-through.
    // Mirrors OverView.jsx:407 — V1 spreads `...pmsConfirmStepState` back into the step
    // so its disabled/loading/label flags surface to the wizard's Finish button.
    const [pmsConfirmStepState, setPmsConfirmStepState] = useState({});

    // Dentally booking state — mirrors OverView.jsx:406, :411-413, :694, :1455-1462.
    // Same shape as PMS: a 2-step wizard (AVAILABILITY → CONFIRM) inside BOOK_APPOINTMENT.
    // Added 2026-06-06 (Task #63) — V1 has a Dentally branch on Dentally-enabled practices,
    // V2 was missing it entirely. useDentallyFlow defaults true so Dentally practices land
    // in the Dentally wizard by default; handleToggleBookingMode flips to CRM/PMS-style.
    const [dentallySlot, setDentallySlot] = useState(null);
    const [selectedDentallyTreatment, setSelectedDentallyTreatment] = useState(null);
    const [dentallyAssignees, setDentallyAssignees] = useState([]);
    const [useDentallyFlow, setUseDentallyFlow] = useState(true);
    const [followUpConfirmStepState, setFollowUpConfirmStepState] = useState({});
    const [dentallyBookingStep, setDentallyBookingStep] = useState('AVAILABILITY'); // 'AVAILABILITY' | 'PATIENT_DETAILS' | 'CONFIRM'
    const [dentallyPatientData, setDentallyPatientData] = useState(null);
    const [bookingDiagnostic, setBookingDiagnostic] = useState(null);
    const [bookingDiagnosticAttemptGeneration, setBookingDiagnosticAttemptGeneration] = useState(0);
    const bookingDiagnosticScopeKey = [open, practiceId, resolveContactLeadId(leadDetail)].join(':');
    const bookingDiagnosticScopeRef = useRef(bookingDiagnosticScopeKey);
    const bookingDiagnosticAttemptRef = useRef(0);

    if (bookingDiagnosticScopeRef.current !== bookingDiagnosticScopeKey) {
        bookingDiagnosticScopeRef.current = bookingDiagnosticScopeKey;
        bookingDiagnosticAttemptRef.current += 1;
    }

    const handleBookingDiagnosticChange = useCallback((diagnostic) => {
        if (bookingDiagnosticScopeRef.current !== bookingDiagnosticScopeKey) return;
        if (bookingDiagnosticAttemptRef.current !== bookingDiagnosticAttemptGeneration) return;
        setBookingDiagnostic(diagnostic || null);
    }, [bookingDiagnosticAttemptGeneration, bookingDiagnosticScopeKey]);

    const beginBookingDiagnosticAttempt = useCallback(() => {
        const nextGeneration = bookingDiagnosticAttemptRef.current + 1;
        bookingDiagnosticAttemptRef.current = nextGeneration;
        setBookingDiagnosticAttemptGeneration(nextGeneration);
        setBookingDiagnostic(null);
    }, []);

    useEffect(() => {
        setBookingDiagnosticAttemptGeneration(bookingDiagnosticAttemptRef.current);
        setBookingDiagnostic(null);
    }, [bookingDiagnosticScopeKey]);

    // Task #63 follow-up (2026-06-07): post-booking step machine for the DEFAULT (CRM)
    // Book branch. Mirrors V1's OverView.jsx:877-903 step array which renders
    // Booking Details → Personal Details → Payment options → [optional] Dentally
    // Confirmation → Message review & reminders. V2 owns the active-step pointer
    // locally (no equivalent of OpenContactLeadPopup's activeStep+validStep machinery
    // in AttendedPopupShell), so we drive it with one piece of state + an advance
    // callback. PMS + Dentally booking paths use their own 2-step wizards and DO NOT
    // pass through this step machine (V1's OverView.jsx:740-841 confirms PMS/Dentally
    // wizards don't push PersonalDetails/PaymentOption/MessageAndReminder either).
    const [bookFlowStep, setBookFlowStep] = useState('BOOKING_DETAILS');
    // ^ 'BOOKING_DETAILS' | 'PERSONAL_DETAILS' | 'PAYMENT_OPTIONS' | 'DENTALLY_CONFIRMATION' | 'MESSAGE_REMINDER'

    // 2026-06-07 bundle: per-step validity for the multi-step Book wizard. Mirrors the
    // existing setPmsConfirmStepState pattern (V2 line 381) and V1 OverView.jsx:407 —
    // child step pushes its formState.isValid up via onValidityChange so the parent
    // shell can disable the Next/Finish button when the current step is invalid.
    //
    // Default true so V1 step components (which don't implement onValidityChange yet —
    // PersonalDetails / PaymentOption / DentallyPaymentConfirmation / MessageAndReminder)
    // keep behaving like they do today (button enabled). TODO #64b-e: as each V1 step
    // is replaced with its V2-native counterpart, wire its onValidityChange to flip
    // this to false until the step's RHF formState reports valid.
    const [bookStepValid, setBookStepValid] = useState(true);

    // H-3: Archive — ref to Archive's imperative submit
    const archiveRef = useRef(null);

    // H-2: TreatmentAndAppointment — ref to its imperative submit
    const bookingRef = useRef(null);

    // ── CRITICAL: PersonalDetails snapshot for V2 Book wizard ───────────────────
    // Bug observed in smoke (2026-06-08, lead 166703): the user typed a phone
    // number on Step 2 (PersonalDetails) but no PATCH to
    // /api/v1/practices/{p}/leads/{id}/ fired anywhere in the wizard — neither
    // on Next from Step 2 nor on the final Finish booking. The appointment
    // POST succeeded (201) but lead_contact stayed null.
    //
    // V1's PersonalDetails.submit dispatches updatePersonalDetail when the form
    // is modified (PersonalDetails.jsx:186-189), and V2 calls
    // bookingRef.current.submit('next') on Step 2 Next — so on paper the PATCH
    // should fire. In practice it is unreliable across step transitions
    // (RHF defaultValues re-init, leadDetail wipe race, or the form unmounting
    // before the dispatched thunk resolves). Rather than chase the race, we
    // snapshot the form values when the user leaves Step 2 and explicitly
    // re-PATCH on the final Finish booking before the appointment POST. This
    // guarantees the user's edits are persisted regardless of which path
    // PersonalDetails.onSubmit took internally.
    //
    // Payload contract mirrors V1 PersonalDetails.onSubmit
    // (PersonalDetails.jsx:105-184) — same shape, same thunk
    // (updatePersonalDetail), same response handling.
    const personalDetailsSnapshotRef = useRef(null);

    // ── WhatsApp send → task auto-complete (2026-10-13) ─────────────────────────
    // Holds the task whose action panel opened the sidebar WhatsApp composer, so a
    // confirmed send can complete THAT task. Mirrors NewLead.jsx:425 pendingV2TaskIdRef
    // (same lifecycle: set on open, cleared on close, consumed on first use).
    //
    // Why a ref and not the existing sidebarWhatsappItemId state:
    //   • sidebarWhatsappItemId holds a CHECKLIST ITEM id; completion needs the task id
    //     (and, for flat cadence steps, the standalone flag to pick the right thunk).
    //   • it is only populated when a per-practice WhatsApp template MAPPING resolves
    //     (FollowUpPanel.jsx:335, `if (wtm)`); with no mapping it is null, so reusing it
    //     would silently never complete anything on practices without WTM rows.
    //   • a ref avoids a re-render on arm/disarm and cannot go stale inside the
    //     ChatPage send callback the way a captured state value would.
    const waAnchorRef = useRef(createWhatsappTaskAnchor());

    // ── Resilience Case 2 + 3: defensive treatment selection ────────────────────
    // Case 2: when a lead has zero treatments (leadDetail.lead_treatments empty
    // or missing), every consumer of `lead_treatments[0]` previously rendered
    // undefined chips / NaN prices / blank statuses. We now compute a
    // hasNoTreatments flag and fall back to safe defaults; the hero card renders
    // a "No treatment on file" placeholder (see headerNode below).
    //
    // Case 2b (Parrock bug fix): leadDetail is a PROP — callers from the tasks
    // board (TaskBodyView.jsx:buildRowLead) spread task.lead but the tasks
    // endpoint does not guarantee lead_treatments in the nested lead object.
    // Callers from the NewLead table (useLeadTableHelper) store the array under
    // treatmentData, not lead_treatments. In both cases leadDetail arrives with
    // no lead_treatments key and hasNoTreatments fires a false positive.
    //
    // Fix tier 1: fall back to followLead (Redux state.lead.followLead), which is
    // populated by followUpSlice() → GET .../leads/{id}/?slim=true before the
    // popup opens (TaskBodyView:699, NewLead:2140+). Guard by id equality so a
    // stale followLead from a prior popup never papers over a genuinely zero-
    // treatment lead.
    //
    // Fix tier 2 (Path D fix — leads-table contact-button): some entry paths never
    // dispatch followUpSlice before opening (e.g. NewLead.jsx case 'contactLead' →
    // onOpenContactLead(row) with no prior followUpSlice call). For those paths
    // followLead is null or stale, so tier-1 falls through to [] and triggers a
    // false-positive "No treatment on file". Self-fetch: FollowUpV2 requests the
    // slim endpoint itself when leadDetail.lead_treatments is absent AND
    // followLead doesn't match the open lead. This makes treatment resolution
    // path-agnostic — any future entry path gets correct data without needing to
    // dispatch followUpSlice in every caller.
    //
    // Case 3: when a lead has multiple treatments at the same status (e.g. two
    // separate NEW treatments — Invisalign + Implants), the v2-flat composite-id
    // footgun (memory project-v2flat-composite-id-footgun) means we'd silently
    // pick treatments[0] regardless of which one the user actually wants to
    // follow up on. We expose a picker chip strip above the hero card so the
    // user can swap which treatment is the active one for this session.

    // selfFetchedLead — populated by the tier-2 self-fetch below when neither
    // leadDetail nor followLead carry lead_treatments for the open lead.
    const [selfFetchedLead, setSelfFetchedLead] = useState(null);

    // Lead identity, path-agnostic. Table rows key off `id`; task / reminder
    // rows (getRowLeadData.setRowLeadData) carry only `lead_id`, so reading
    // `leadDetail?.id` alone left those entry paths with no lead id at all and
    // every lead-scoped fetch below silently never fired.
    const anchorLeadId = leadDetail?.id ?? leadDetail?.lead_id ?? null;

    // ── Path-agnostic lead resolution ──────────────────────────────
    // THE hero data source. Every entry point hands FollowUpV2 a different
    // shape (full lead detail / leads-table row / task row) and the hero used
    // to read nested fields straight off it — so lead_source, lead_contact,
    // tags and the enquiry note were undefined on the row shapes and the same
    // lead rendered differently depending on which button opened it.
    // resolveContactLead folds the tier chain that already existed here for
    // lead_treatments (leadDetail → id-matched followLead → id-matched
    // selfFetchedLead) over EVERY hero field, and adds a free tier-0: table
    // rows already carry the untouched API lead at `originalData`.
    //
    // It is a PROJECTION, not a superset of leadDetail — use it for hero
    // derivations and diagnostics only, never as a replacement lead object
    // handed down to children.
    const resolvedLead = useMemo(
        () => resolveContactLead({ leadDetail, followLead, selfFetchedLead }),
        [leadDetail, followLead, selfFetchedLead]
    );

    const bookingPatient = useMemo(() => {
        const patientId = resolveContactLeadId(leadDetail);
        return [leadDetail, leadDetail?.originalData, followLead, selfFetchedLead].find(
            candidate => candidate && patientId !== null && resolveContactLeadId(candidate) === patientId && 'lead_contact' in candidate
        );
    }, [leadDetail, followLead, selfFetchedLead]);

    const bookingPatientSeedRef = useRef(null);

    // Does ANY tier hold a real lead-detail-shaped object for this lead? Keyed
    // on shape, not on values: a lead whose lead_source is legitimately null
    // must not look "incomplete" forever and re-fetch on every open.
    const hasFullLeadShape =
        (!!leadDetail && ('lead_contact' in leadDetail || 'lead_source' in leadDetail))
        || resolveContactLeadId(leadDetail?.originalData) === anchorLeadId
        || (!!followLead && resolveContactLeadId(followLead) === anchorLeadId)
        || (!!selfFetchedLead && resolveContactLeadId(selfFetchedLead) === anchorLeadId);

    // Drives the self-fetch below. Widened from treatments-only: a task row
    // with neither `originalData` nor a matching followLead is missing contact
    // and source as well, which is what emptied the hero on those paths.
    const resolvedLeadIncomplete =
        (anchorLeadId !== null && !hasFullLeadShape)
        || !Array.isArray(resolvedLead.lead_treatments);

    // Tier-3 self-fetch: the popup asks for the lead itself when no entry path
    // supplied it. Mirrors the slim endpoint used by followUpSlice
    // (appointmentThunks.js:525). Runs AT MOST ONCE per lead — the guard ref is
    // load-bearing now that the trigger reads resolvedLead, which the fetch
    // result feeds back into; without it the effect would re-arm itself.
    const selfFetchAttemptedRef = useRef(null);
    useEffect(() => {
        if (selfFetchAttemptedRef.current !== null && selfFetchAttemptedRef.current !== anchorLeadId) {
            // Switched lead — drop the previous lead's self-fetch.
            selfFetchAttemptedRef.current = null;
            setSelfFetchedLead(null);
        }
        if (!anchorLeadId || !practiceId || !resolvedLeadIncomplete) return () => {};
        if (selfFetchAttemptedRef.current === anchorLeadId) return () => {};
        selfFetchAttemptedRef.current = anchorLeadId;

        let cancelled = false;
        // Promise.resolve wrapper: callApi is mocked in several suites and can
        // hand back undefined — a bare .then() would throw inside the effect.
        Promise.resolve(callApi(`/api/v1/practices/${practiceId}/leads/${anchorLeadId}/?slim=true`, 'GET'))
            .then((resp) => {
                if (!cancelled && resp?.ok) setSelfFetchedLead(resp.data);
            })
            .catch(() => {});

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorLeadId, practiceId, resolvedLeadIncomplete]);

    const allLeadTreatments = resolvedLead.lead_treatments || [];
    const hasNoTreatments = allLeadTreatments.length === 0;

    // Anchor treatment id — the specific treatment the task / follow-up is about.
    // TaskBodyView.buildRowLead() sets `treatmentData` (from task.lead_treatment)
    // and `treatment_id` on the rowLead that becomes leadDetail. We prefer
    // treatmentData.id because it's the full FK object; treatment_id is the flat
    // string copy also written by buildRowLead. Either is sufficient — we only
    // need the id to index into allLeadTreatments once the array hydrates.
    // Prefer the explicit leadTreatmentId prop (passed by the launcher) — it is
    // known synchronously at mount, so the active treatment is deterministic on
    // first render and there is no wrong-treatment flash while leadDetail/followLead
    // hydrate. Falls back to the legacy late-inference when the prop is absent.
    const anchorTreatmentId = leadTreatmentId || leadDetail?.treatmentData?.id || leadDetail?.treatment_id || null;
    const leadNextActionFooter = deriveLeadNextActionFooter({
        items,
        activeTreatmentId: anchorTreatmentId,
        isActive: activeTab === V2_TASKS_ACTION_LABEL,
    });

    // Active treatment index — defaults to 0, user can pick from chip strip when
    // 2+ treatments share the current status. Snap-back guard handles a new lead
    // being loaded into the same modal instance.
    //
    // When allLeadTreatments hydrates from the followLead fallback (i.e. leadDetail
    // arrived without lead_treatments but followLead does have them), we seed the
    // index to the treatment matching anchorTreatmentId rather than blindly picking
    // [0]. This prevents the composite-id / [0] footgun when a lead has e.g.
    // Invisalign (id=100) + Implants (id=139615) both NEW and the task points at
    // the second one — we resolve id=139615, find idx=1, and open on the right one.
    // Seed the index from the anchor SYNCHRONOUSLY on mount when the treatments
    // are already available (the common launcher case: leadDetail carries
    // lead_treatments + a leadTreatmentId prop). This makes the FIRST render show
    // the selected treatment — no flash of treatment[0]'s tasks before the effect
    // below corrects it. The effect still handles the async case (treatments
    // hydrating later from followLead / the slim self-fetch).
    const [activeTreatmentIdx, setActiveTreatmentIdx] = useState(() => {
        if (anchorTreatmentId && allLeadTreatments.length) {
            const i = findTreatmentIdxById(allLeadTreatments, anchorTreatmentId);
            if (i !== -1) return i;
        }
        return 0;
    });
    useEffect(() => {
        const len = allLeadTreatments.length;
        if (len === 0) return;
        if (anchorTreatmentId) {
            const matchIdx = findTreatmentIdxById(allLeadTreatments, anchorTreatmentId);
            if (matchIdx !== -1 && matchIdx !== activeTreatmentIdx) {
                setActiveTreatmentIdx(matchIdx);
                return;
            }
        }
        // Snap-back: keep index in bounds (e.g. new lead loaded into same popup).
        if (activeTreatmentIdx >= len) setActiveTreatmentIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allLeadTreatments.length, anchorTreatmentId]);
    const activeTreatment = allLeadTreatments[activeTreatmentIdx] || null;

    const treatment = activeTreatment?.treatment || '—';
    const treatmentId = activeTreatment?.id ?? null;
    const status = activeTreatment?.status || 'NEW';
    // R-1: capitalize each name part — mirrors CAA.jsx:480 capitalizeFirstLetter usage
    const leadName = leadDetail
        ? `${capitalizeFirstLetter(resolvedLead.first_name || '')} ${capitalizeFirstLetter(resolvedLead.last_name || '')}`.trim()
        : '';
    const currentLeadId = anchorLeadId;

    useEffect(() => {
        setDentallyBookingStep('AVAILABILITY');
        setDentallyPatientData(null);
        setDentallySlot(null);
        setSelectedDentallyTreatment(null);
        setDentallyAssignees([]);
        setFollowUpConfirmStepState({});
    }, [practiceId, currentLeadId]);

    // Preferred-contact preferences — read from leadDetail.lead_contact.*
    // Mirrors FollowUp.jsx:281-294 (contactInfo derivation)
    const preferredMethod = resolvedLead.lead_contact?.preferred_contact_method || null;
    const preferredDays = resolvedLead.lead_contact?.preferred_contact_days || [];
    const preferredPeriods = resolvedLead.lead_contact?.preferred_contact_periods || [];

    // ── Hero V2 extra fields — mirrors V1 OpenContactLeadPopup header data ──────
    // Contacted count → Follow-up stage chip (FU 1 / FU 2 / FU 3+)
    // Mirrors OpenContactLeadPopup.jsx:575-576
    const heroContactedCount = resolvedLead.contacted_count
        ?? allLeadTreatments?.[0]?.lead_contacted_counts?.find?.(
            (c) => c.status === 'NEW'
        )?.count
        // Leads-table and task rows flatten the count onto this column.
        ?? leadDetail?.lead_treatments__lead_contacted_counts__count
        ?? 0;
    // FU chip label — "FU 1" / "FU 2" / "FU 3+" mirrors V1 chip format
    // (OpenContactLeadPopup.jsx:603-641 JOURNEY_STAGES label pattern)
    const heroFollowUpLabel = (() => {
        if (heroContactedCount === 0) return null;
        if (heroContactedCount === 1) return 'FU 1';
        if (heroContactedCount === 2) return 'FU 2';
        return 'FU 3+';
    })();

    // Phone + email — surfaced prominently in hero (V1 shows both in header)
    const heroPhone = resolvedLead.lead_contact?.phone || null;
    const heroEmail = resolvedLead.lead_contact?.email || null;

    // Source / referral / tags — from leadDetail.lead_source and leadDetail.tags
    const heroSourceType = resolvedLead.lead_source?.type || null;
    const heroReferral   = resolvedLead.lead_source?.referral || null;
    const heroTags       = resolvedLead.tags || [];

    // Enquiry message — the original web-form note from the lead's contact record.
    // V1 source: OverView.jsx:1770 (leadDetail?.lead_contact?.note)
    // lead_contact.note is the web-form enquiry on most leads; leads whose
    // enquiry was captured as a note instead keep it on lead_notes[0]
    // (ArchiveDetailView.jsx:342 reads the same pair).
    const heroEnquiryText = resolvedLead.lead_contact?.note
        || resolvedLead.lead_notes?.[0]?.additional_notes
        || '';
    const ENQUIRY_TRUNCATE = 200;
    const [enquiryExpanded, setEnquiryExpanded] = useState(false);

    // ── Data fetches on open — mirrors OpenConsultationPopup.jsx:69-77
    useEffect(() => {
        if (!practiceId || !currentLeadId) return;
        setNotesLength([]);
        dispatch(getLeadNotes(currentLeadId, practiceId));
        dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'leadNotes', currentLeadId, '', '', '', 'counter', '', false, true));
        dispatch(getChoicesList(practiceId));
        dispatch(followUpSlice(currentLeadId, practiceId, 'LeadDetail'));
    }, [currentLeadId, practiceId]);

    // ── Treatment data for OverviewTab — mirrors OpenConsultationPopup.jsx:79-91
    useEffect(() => {
        const fetchData = async () => {
            if (!practiceId || !currentLeadId) return;
            try {
                const resp = await callApi(`/api/v1/practices/${practiceId}/leads/${currentLeadId}/treatments`, 'GET');
                setTreatmentData(resp?.data?.results ?? []);
            } catch (error) {
                console.error('Error fetching treatment data:', error);
            }
        };
        fetchData();
    }, [practiceId, currentLeadId]);

    // ── Combine notes arrays — mirrors OpenConsultationPopup.jsx:93-110
    useEffect(() => {
        const combineNotes = [];
        if (leadNotes?.length > 0) {
            combineNotes.push(...leadNotes.filter((el) => el.additional_notes !== ''));
        }
        if (leadNotesArr?.results?.length > 0) {
            combineNotes.push(...leadNotesArr.results.filter((el) => el.note !== ''));
        }
        if (combineNotes.length) {
            setNotesLength(
                combineNotes.sort(
                    (a, b) =>
                        new Date(moment(a.updated_at, 'DD-MM-YYYY HH:mm:ss')) -
                        new Date(moment(b.updated_at, 'DD-MM-YYYY HH:mm:ss'))
                )
            );
        }
    }, [leadNotes, leadNotesArr]);

    // Reset pulse counter when the popup closes — mirrors OpenConsultationPopup.jsx:113-115
    useEffect(() => {
        if (!open) setSidebarPulse(0);
    }, [open]);

    // On open, route to the WhatsApp tab when the lead came in via WhatsApp.
    //
    // ContactLeadPopupRouter returns null when !open, so FollowUpV2 always mounts
    // fresh for each popup open. The lazy useState initializer (computeInitialSidebarTab)
    // handles the common case where leadDetail (including lead_source.type) is already
    // available from the table row at mount time.
    //
    // This effect handles the edge case where leadDetail arrives with a partial payload
    // at mount (no lead_source yet) and the full object arrives shortly after. We gate
    // on leadDetail.id being present so we never commit a tab based on a null/loading
    // skeleton — this prevents the 'overview' → 'whatsapp' content flash for WhatsApp
    // leads when source info loads asynchronously.
    //
    // Deps intentionally omit leadDetail.lead_source.type: once we have a real id we
    // commit the tab immediately and do not re-fire on subsequent partial updates. The
    // layout width is decoupled from sidebarTab (see AttendedFullscreenWrapper.jsx),
    // so even if the content tab changes once on load, no layout jump occurs.
    useEffect(() => {
        if (!open) return;
        if (!leadDetail?.id) return; // wait for real data — don't commit from a loading skeleton
        const isWhatsappSource = leadDetail?.lead_source?.type === 'WHATSAPP';
        setSidebarTab(isWhatsappSource ? 'whatsapp' : 'overview');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, leadDetail?.id]);

    // 2026-06-07 #85: mount-time redux reset — mirrors V1 OpenContactLeadPopup.jsx:140-144.
    // V1's wrapper resets `markAsComplete` to false on every mount; without this V2
    // could inherit a stale `markAsComplete=true` from a prior Archive/Skip in the
    // same session. That stale flag combined with the saveNewLeadDetail dispatch
    // inside V2BookingDetailsStep.onSubmit was triggering the NewLead.jsx:1738-1768
    // case 'contactLead' useEffect, which short-circuits the 5-step Book wizard by
    // firing updateLeadDetail → addAppointment(status='finish') → handleClose('finish').
    // Also reset updateSteps/updateBookingSteps so a previous lead's Booked-flow step
    // priming doesn't leak (mirrors V1:393, 396).
    useEffect(() => {
        if (open) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateSteps(null));
            dispatch(updateBookingSteps(null));
        }
    }, [open]);

    // ── Debug metrics (superadmin only) — computed unconditionally for stable deps
    const debugMetrics = useMemo(() => {
        const allTemplates = (rawGroups || []).flatMap((g) => g.tasks || []);

        const attendedTemplates = allTemplates.filter((t) => {
            const sit = Array.isArray(t.show_in_tables) ? t.show_in_tables : [];
            return sit.some((s) => typeof s === 'string' && s.includes('Attended'));
        });
        const v2Templates = allTemplates.filter((t) => {
            const sit = Array.isArray(t.show_in_tables) ? t.show_in_tables : [];
            return sit.some((s) => typeof s === 'string' && s.includes('Dashboard V2'));
        });

        const groupsWithItems = (rawGroups || []).filter(
            (g) => (g.tasks || []).some((t) => (t.checklist_items || []).length > 0)
        );

        const sampleTask = allTemplates.find((t) => (t.checklist_items || []).length > 0)
            || allTemplates[0]
            || null;

        return {
            groupsCount: (rawGroups || []).length,
            templatesCount: allTemplates.length,
            attendedTemplatesCount: attendedTemplates.length,
            v2TemplatesCount: v2Templates.length,
            matchingGroupsCount: groupsWithItems.length,
            groupsWithItemsCount: groupsWithItems.length,
            sampleTask: sampleTask ? {
                id: sampleTask.id,
                title: sampleTask.title || sampleTask.name || '—',
                keys: Object.keys(sampleTask),
                checklist_items_length: (sampleTask.checklist_items || []).length,
            } : null,
        };
    }, [rawGroups]);

    // ── Sync assigneeSelected when followLead updates — mirrors CAA.jsx:473-477
    useEffect(() => {
        if (followLead?.assignees) {
            setAssigneeSelected(followLead.assignees || []);
        }
    }, [followLead]);

    // ── Hero card helpers — mirror ConsultationAttendedAppointment.jsx:459-471 ──
    // V2 leads are in NEW/follow-up status, so their relevant appointment is BOOKED
    // (not ATTENDED). appointmentFilter for the badge uses the same subset.
    const followLeadFilterBooked = (followLead?.lead_appointment || []).filter(
        (el) => el.status === 'BOOKED' || el.status === 'CONSULTATION_BOOKED'
    );
    // appointmentFilter passed to AttendedFullscreenWrapper for the FollowUpBadge.
    // Mirrors OpenAttendedPopup.jsx:210-212 but targets BOOKED appointments.
    const v2AppointmentFilter = (appts) =>
        appts.filter((el) => el.status === 'BOOKED' || el.status === 'CONSULTATION_BOOKED');
    const getV2TreatmentDateTime = () =>
        getTreatmentDate(followLeadFilterBooked?.[0]?.datetime) || '';
    const getV2TreatmentAssignees = () => followLeadFilterBooked?.[0]?.assignees;

    // ── Badge progress from local items array — avoids a second useGuidedChecklist call.
    // F-1: checklistDone = items not OPEN (complete / skipped / queued);
    //      checklistTotal = all items. Mirrors OpenAttendedPopup.jsx:59-62 intent.
    const badgeChecklistTotal = items.length;
    const badgeChecklistDone = items.filter((i) => i.completion_state !== 'OPEN').length;

    // ── OutcomePanel "Mark complete" — immediate-persist on the shared hook instance.
    // The OutcomePanel auto-complete branch (WhatsApp / Phone / Next Action) renders a
    // "Mark complete" button that completes the oldest open matching checklist item.
    // The reminder body's own card/hero completion lives in ScheduleReminderTaskSection;
    // this thin handler covers only the OUTCOME-box path, dispatching the same
    // completeChecklistItem / completeTask thunk + refreshing the shared `checklist`
    // instance so the badge + section stay in sync. Mirrors the body's handler
    // (sans the body-local ack/featured state, which is irrelevant in OUTCOME mode).
    const handleOutcomeMarkComplete = useCallback((item) => {
        if (!item?.taskId) return;
        if (item.isStandaloneTask) {
            dispatch(completeTask(practiceId, item.taskId, ({ success }) => {
                if (success) refresh({ bustCache: true });
            }));
            return;
        }
        if (!item?.itemId) return;
        const payload = item.resolvedAction ? { resolved_action: item.resolvedAction } : null;
        dispatch(completeChecklistItem(
            practiceId, item.taskId, item.itemId,
            ({ success }) => { if (success) refresh({ bustCache: true }); },
            payload,
            CHECKLIST_COMPLETION_STATE.YES,
        ));
    }, [practiceId, refresh]);

    // ── Arm / disarm the WhatsApp send→complete anchor ──────────────────────────
    // Called by ScheduleReminderTaskSection when a task's action panel opens (arm)
    // and when it closes or completes by another route (disarm). ALWAYS set-or-clear:
    // opening a non-WhatsApp task must wipe a previous WhatsApp task's anchor,
    // otherwise a later send would close the wrong step.
    const armWhatsappTask = useCallback((item) => {
        if (item) waAnchorRef.current.arm(item);
        else waAnchorRef.current.disarm();
    }, []);

    // ── Confirmed WhatsApp send → complete the armed task ───────────────────────
    // Wired to FollowUpWhatsappChat's onMessageConfirmed (NOT onMessageSent, which
    // ChatPage.jsx:270-271 documents as firing optimistically BEFORE the API
    // round-trip — using it would complete tasks for sends that then fail).
    //
    // One-shot: the anchor is consumed before dispatching, so sending three messages
    // while one task is open completes that task once, not three times. Matches
    // NewLead.handleConsultationSendComplete (NewLead.jsx:1513-1522).
    //
    // A send from the WhatsApp sidebar tab opened directly — no task actioned — leaves
    // the anchor null and completes nothing, which is today's behaviour and intended.
    const handleWhatsappConfirmedSend = useCallback(() => {
        // consume() is one-shot: it returns the armed context AND clears it, so a
        // second message sent against the same open task completes nothing further.
        const plan = planWhatsappCompletion(waAnchorRef.current.consume());
        if (plan.kind === 'none') return;
        const done = ({ success }) => {
            if (!success) return;
            refresh({ bustCache: true });
            window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', {
                detail: { source: 'whatsapp-send-autocomplete' },
            }));
        };
        if (plan.kind === 'task') {
            dispatch(completeTask(practiceId, plan.taskId, done));
            return;
        }
        dispatch(completeChecklistItem(
            practiceId, plan.taskId, plan.itemId,
            done,
            null,
            CHECKLIST_COMPLETION_STATE.YES,
        ));
    }, [practiceId, refresh]);


    // ── Sidebar tab handler — mirrors OpenConsultationPopup.jsx:117
    const handleSidebarTabChange = useCallback((event, newValue) => setSidebarTab(newValue), []);

    // ── onOpenSidebarTab — called by ActionPanel for email/whatsapp routing ────
    // Mirrors the onOpenSidebarTab prop wired in AttendedAppointment.jsx:340.
    // Called as onOpenSidebarTab(tab) or onOpenSidebarTab(tab, { templateId }).
    // Source: ActionPanel.jsx:167,176 (email tab open + template resolve).
    const handleOpenSidebarTab = useCallback((tab, opts) => {
        setSidebarTab(tab);
        setSidebarPulse((p) => p + 1);
        if (opts?.templateId != null) {
            // Route the resolved template id to the matching tab's pre-fill slot.
            // Email = setSidebarEmailTemplateId, WhatsApp = setSidebarWhatsappTemplateId.
            // Anything else: ignore.
            if (tab === 'email') {
                setSidebarEmailTemplateId(String(opts.templateId));
            } else if (tab === 'whatsapp') {
                setSidebarWhatsappTemplateId(String(opts.templateId));
            }
        }
        // WTM mapped-template draft: FollowUpPanel resolves a whatsapp template
        // (id + name) and routes it here. ChatPage matches it id-first in the
        // loaded template list and sends via the approved-template path (works
        // even when the window is closed).
        // ALWAYS set-or-CLEAR: FollowUpPanel fires ('whatsapp') with no opts first
        // and only re-fires with a templateName when a mapping exists — so a
        // no-opts (or mapping-less) call must CLEAR any previous item's template,
        // otherwise a stale mapped draft leaks onto a different checklist item.
        if (tab === 'whatsapp') {
            const wa = resolveWhatsappSidebarTemplate(opts);
            setSidebarWhatsappTemplateName(wa.name);
            setSidebarWhatsappTemplateWaId(wa.waId);
            // Item id rides alongside the template (set-or-clear with it).
            setSidebarWhatsappItemId(opts?.itemId != null ? String(opts.itemId) : null);
        }
    }, []);

    // H-6: body overflow-hidden defensive cleanup on unmount.
    // V1 removes it explicitly in handleClose — V2 must also do this on unmount
    // in case any parent forgets to call onClose (keep-mounted pattern, etc.).
    useEffect(() => () => {
        document.documentElement.classList.remove('overflow-hidden');
        bookingDiagnosticAttemptRef.current += 1;
    }, []);

    // Task #63 follow-up (2026-06-07): advance the booked-flow step machine.
    // Each step component calls handleNext(true) on successful submit. We advance to
    // the next step matching V1's OverView.jsx:877-903 order. When MessageAndReminder
    // (the final step) calls handleNext, we close the modal + reload the leads table.
    // The success toast for the booking itself is fired by the addAppointment thunk
    // (appointmentThunks.js:96 'Appointment added successfully') back at the FIRST step;
    // by the time the user reaches MessageAndReminder it has either rendered and
    // dismissed naturally or is overlaid on the dashboard after close. The relevant UX
    // win is the user SEEING the post-booking flow (Payment + Messages), which is what
    // was reported missing.
    //
    // isDentallyBooking source: state.lead (mirrors OverView.jsx:437 destructure). When
    // it's true AND dentallyEnabledGlobal is true, we insert DENTALLY_CONFIRMATION
    // between PAYMENT_OPTIONS and MESSAGE_REMINDER (OverView.jsx:894-899).
    const advanceBookFlow = useCallback(() => {
        if (bookFlowStep === 'BOOKING_DETAILS') {
            setBookFlowStep('PERSONAL_DETAILS');
        } else if (bookFlowStep === 'PERSONAL_DETAILS') {
            setBookFlowStep('PAYMENT_OPTIONS');
        } else if (bookFlowStep === 'PAYMENT_OPTIONS') {
            // Optional Dentally Confirmation slot — V1 only renders it for
            // dentallyEnabledGlobal && isDentallyBooking practices (OverView.jsx:894).
            if (dentallyVisible && isDentallyBooking) {
                setBookFlowStep('DENTALLY_CONFIRMATION');
            } else {
                setBookFlowStep('MESSAGE_REMINDER');
            }
        } else if (bookFlowStep === 'DENTALLY_CONFIRMATION') {
            setBookFlowStep('MESSAGE_REMINDER');
        } else if (bookFlowStep === 'MESSAGE_REMINDER') {
            // Final step — wizard complete. Refresh table + close modal.
            if (handleReloadLeads) handleReloadLeads();
            // handleClose is hoisted below; call via the existing close prop pattern.
            if (onClose) onClose('finish');
        }
    }, [bookFlowStep, dentallyVisible, isDentallyBooking, handleReloadLeads, onClose]);

    // 2026-06-07 bundle: regressBookFlow — inverse of advanceBookFlow. Walks the same
    // sequence backwards with the same Dentally branching (PAYMENT_OPTIONS ↔
    // DENTALLY_CONFIRMATION inserted only when dentallyEnabledGlobal && isDentallyBooking).
    // Each step component owns its own RHF/useState so unmount-on-back is what would
    // reset form state — therefore we only flip bookFlowStep here and rely on React's
    // standard mount/unmount of the conditional <stepNode> branch in OutcomePanel.jsx:2289-2311.
    // Caveat: each step DOES unmount when leaving (the if-else chain renders a single
    // stepNode), so form state is lost on Back today — same shape as advanceBookFlow's
    // forward flow. Persisting that state is out of scope for this bundle; flagged for
    // a follow-up (cross-step lifted state via FollowUpV2 — see brief).
    const regressBookFlow = useCallback(() => {
        if (bookFlowStep === 'PERSONAL_DETAILS') {
            setBookFlowStep('BOOKING_DETAILS');
        } else if (bookFlowStep === 'PAYMENT_OPTIONS') {
            setBookFlowStep('PERSONAL_DETAILS');
        } else if (bookFlowStep === 'DENTALLY_CONFIRMATION') {
            setBookFlowStep('PAYMENT_OPTIONS');
        } else if (bookFlowStep === 'MESSAGE_REMINDER') {
            // Mirror advanceBookFlow's Dentally branch at line 911-915 in reverse.
            if (dentallyVisible && isDentallyBooking) {
                setBookFlowStep('DENTALLY_CONFIRMATION');
            } else {
                setBookFlowStep('PAYMENT_OPTIONS');
            }
        }
        // BOOKING_DETAILS has no predecessor — Back button isn't rendered there.
    }, [bookFlowStep, dentallyVisible, isDentallyBooking]);

    // Gap #1 (Task #63): Dentally booking-mode toggle handler.
    // Mirrors OverView.jsx:1455-1462 verbatim. When the user toggles to CRM mode
    // we reset all Dentally-specific selections so a half-filled Dentally form
    // doesn't leak into the CRM (TreatmentAndAppointment) submission.
    const handleToggleBookingMode = useCallback((useDentally) => {
        setUseDentallyFlow(useDentally);
        if (!useDentally) {
            beginBookingDiagnosticAttempt();
            setDentallySlot(null);
            setSelectedDentallyTreatment(null);
            setDentallyAssignees([]);
            setDentallyBookingStep('AVAILABILITY');
            setDentallyPatientData(null);
            setFollowUpConfirmStepState({});
        }
    }, [beginBookingDiagnosticAttempt]);

    // H-2: When BOOK_APPOINTMENT is chosen, prime tableRowLead in Redux so
    // TreatmentAndAppointment can read it. Mirrors OverView.jsx tableData().
    const primeTableLead = useCallback(() => {
        // PersonalDetails reads the booking draft, not the hero's resolved lead.
        // Seed it once on Book so a fresh reload or another patient's old draft
        // cannot leave this existing patient's contact blank or misbound.
        bookingPatientSeedRef.current = bookingPatient ? resolveContactLeadId(bookingPatient) : null;
        dispatch(beginExistingLeadBooking(followLead));
        // Every field must come from the SELECTED treatment (activeTreatment), never
        // lead_treatments[0] — with two same-name treatments (old CLOSED + new round)
        // [0] primes the wrong price/status/count even when treatment_id is right.
        dispatch(tableLead({
            lead_id: leadDetail?.id || '',
            treatment_id: treatmentId || '',
            price: activeTreatment?.price || '',
            first_name: leadDetail?.first_name || '',
            last_name: leadDetail?.last_name || '',
            status: activeTreatment?.status || '',
            treatment_name: activeTreatment?.treatment || '',
            lead_treatments__lead_contacted_counts__count:
                activeTreatment?.lead_contacted_counts?.find(
                    (item) => item.status === activeTreatment?.status
                )?.count || 0,
            treatmentData: leadDetail?.lead_treatments,
        }));
    }, [leadDetail, treatmentId, activeTreatment, bookingPatient, followLead]);

    const handleTabChange = useCallback((tabValue) => {
        setActiveTab(tabValue);

        const action = V2_ACTIONS.find((a) => a.label === tabValue);

        // Gap F: checklist-bound outcomes (WHATSAPP_SENT / LOG_PHONE_CALL / LEAD_NEXT_ACTION)
        // keep the checklist visible (flowStep stays CONTENT) and open the ActionPanel for
        // the matched item directly — same as clicking the card's action button.
        // This mirrors CAA where AttendedGuidedChecklistV2 stays mounted inside the
        // 'Reminder & Follow-up' outcome while the ActionPanel opens beside it.
        // BOOK / ARCHIVE / UNABLE_TO_CONTACT still switch to OUTCOME_CHOSEN (full replacement).
        if (action && AUTO_COMPLETE_TYPES.has(action.key)) {
            // Stay on CONTENT — checklist stays visible; ActionPanel inside
            // ScheduleReminderTaskSection handles its own item for the matched type.
            setFlowStep('CONTENT');
            return;
        }

        // Non-checklist outcomes: switch to OUTCOME_CHOSEN (full-replacement panel)
        setFlowStep('OUTCOME_CHOSEN');

        // Intentionally NOT setting 'contactLead' on the BOOK_APPOINTMENT path here:
        // doing so activates the NewLead.jsx:1741-1746 `case 'contactLead'` branch
        // which dispatches updateLeadDetail → may be implicated in the Book 400.
        // Until the 400 response body is captured, keep BOOK out of this gate.
        // H-2: prime Redux tableRowLead when Book is chosen.
        // Task #63 — also reset BOTH wizard step indicators so a previous lead's
        // CONFIRM step doesn't open straight into the next lead's booking,
        // and reset Dentally selections (mirrors OverView.jsx:688-695 close-side reset
        // applied at booking-entry time too, since V2 keep-mounts FollowUpV2).
        if (action?.key === 'BOOK_APPOINTMENT') {
            beginBookingDiagnosticAttempt();
            primeTableLead();
            setPmsBookingStep('AVAILABILITY');
            setPmsSlot(null);
            setDentallyBookingStep('AVAILABILITY');
            setDentallyPatientData(null);
            setDentallySlot(null);
            setSelectedDentallyTreatment(null);
            setDentallyAssignees([]);
            setUseDentallyFlow(true);
            setFollowUpConfirmStepState({});
            setPmsConfirmStepState({});
            // Task #63 follow-up — reset the booked-flow step machine so the next
            // Book entry starts at BOOKING_DETAILS rather than mid-wizard.
            setBookFlowStep('BOOKING_DETAILS');
        }
    }, [beginBookingDiagnosticAttempt, primeTableLead, openItemsByType]);

    const handleClose = useCallback((reason, data) => {
        // Reset to TASKS chip default (matches initial useState above) so when the
        // modal re-opens on another lead, the leftmost chip is highlighted again
        // rather than no-chip-selected.
        setActiveTab(V2_TASKS_ACTION_LABEL);
        setFlowStep('CONTENT');
        // Gap #7 (Task #63): reset ALL booking state on close — mirrors
        // OverView.jsx:688-695 verbatim. Previously only pmsSlot + pmsBookingStep
        // were reset, so practitioner/date carried over to the next lead and the
        // Dentally branch never reset at all. Now matches V1.
        setPmsSlot(null);
        setPmsPractitioner(null);
        setPmsDate(null);
        setPmsBookingStep('AVAILABILITY');
        setPmsConfirmStepState({});
        setDentallySlot(null);
        setSelectedDentallyTreatment(null);
        setDentallyAssignees([]);
        setUseDentallyFlow(true);
        setFollowUpConfirmStepState({});
        setDentallyBookingStep('AVAILABILITY');
        setDentallyPatientData(null);
        beginBookingDiagnosticAttempt();
        // Task #63 follow-up — reset booked-flow step machine on close.
        setBookFlowStep('BOOKING_DETAILS');
        // Note: showCustomReminderToggle, featuredItem, actionPanelItem,
        // showRescheduleBanner, bannerRescheduleOpen, optimisticPausedInfo now live
        // inside ScheduleReminderTaskSection (keyed by currentLeadId) — no reset needed here.
        // Clear PersonalDetails snapshot on every close so a reopened wizard
        // (different lead) starts clean. See personalDetailsSnapshotRef RCA.
        personalDetailsSnapshotRef.current = null;
        // Disarm the WhatsApp send→complete anchor so a send made after reopening
        // on a different lead can never complete the previous lead's task.
        waAnchorRef.current.disarm();
        // H-6: explicit cleanup on each close path (unmount useEffect covers the rest)
        document.documentElement.classList.remove('overflow-hidden');
        if (onClose) onClose(reason, data);
    }, [beginBookingDiagnosticAttempt, onClose]);

    // Non-Dentally (default/CRM) booking wizard: the MESSAGE_REMINDER branch below
    // deliberately dispatch(updateSteps(null))'d so addAppointment/updatePersonalDetail's
    // `attendance === 'payment'` gate never fires here (that gate created the
    // double-processing bug this comment used to warn about — see git blame).
    // That means PaymentOption's saved newLeadDetail.payment is otherwise never
    // acted on for this flow, so once the appointment is durably booked we fire it
    // explicitly here — booking stays booked even if the payment/link call fails.
    const processBookingPayment = useCallback((onDone) => {
        const payment = newLeadDetail?.payment;
        const leadId = leadDetail?.id;
        if (!payment || payment.payment_type === 'No' || !leadId || !treatmentId) {
            onDone();
            return;
        }
        dispatch(leadTreatmentPayment(practiceId, leadId, treatmentId, payment, onDone));
    }, [newLeadDetail?.payment, leadDetail?.id, treatmentId, practiceId]);

    const handleFinish = useCallback(async () => {
        const activeAction = V2_ACTIONS.find((a) => a.label === activeTab);

        // H-2: delegate to the active step's imperative submit for Book.
        // Task #63 — branch matches V1 (OverView.jsx:740-852):
        //   • PMS branch (pmsEnabledGlobal && !dentallyEnabledGlobal): 2-step wizard,
        //     submit lives on bookingRef in both steps (Availability advances, Confirm books).
        //   • Dentally branch (!pmsEnabledGlobal && dentallyEnabledGlobal && useDentallyFlow):
        //     same 2-step shape — Availability advances, FollowUpConfirm books.
        //   • Default branch (everything else): single-step TreatmentAndAppointment.
        if (activeAction?.key === 'BOOK_APPOINTMENT') {
            if (!pmsScope.ready) return;
            if (pmsEnabledGlobal && !dentallyVisible) {
                bookingRef.current?.submit?.('finish');
                return;
            }
            if (dentallyCanBook && useDentallyFlow) {
                bookingRef.current?.submit?.('finish');
                return;
            }
            if (bookFlowStep === 'BOOKING_DETAILS') {
                // A thin launcher can finish loading after Book was selected.
                // Hydrate before PersonalDetails mounts, never over later edits.
                if (!bookingPatient) return;
                if (bookingPatientSeedRef.current !== resolveContactLeadId(leadDetail)) {
                    dispatch(saveNewLeadDetail({ personalDetail: { ...bookingPatient }, personalDetailEdited: false }));
                    bookingPatientSeedRef.current = resolveContactLeadId(leadDetail);
                }
            }
            // #91 — Default / CRM path is a multi-step wizard. Mirror V1's
            // OpenContactLeadPopup.jsx:416-417 stepper: only pass 'finish' on the
            // LAST step (MESSAGE_REMINDER). On intermediate steps, pass 'next' so
            // the V1 step components (PersonalDetails / PaymentOption /
            // DentallyPaymentConfirmation) skip their `status==='finish'` branch —
            // which dispatches updateCompleteStatus(true) and short-circuits the
            // modal closed via the markAsComplete listeners.
            // Bug A fix (#93 follow-up): the FINAL step's submit(`finish`) calls
            // handleNext(true) → advanceBookFlow → onClose('finish'), which fires
            // NewLead.handleClose's dispatch(clearLead()) and wipes Redux's
            // followLead/newLeadDetail. The previous attempt put updateLeadDetail
            // AFTER the await — by then state was gone, updateLeadDetail bailed
            // at coreThunks.js:1030 ("Lead data not loaded yet"), and the POST
            // never fired. So:
            //   • On MESSAGE_REMINDER, fire updateLeadDetail FIRST while
            //     Redux still holds followLead + newLeadDetail. updateLeadDetail's
            //     handleClose callback closes the modal AFTER the POST succeeds
            //     (mirroring V1's addAppointment success-callback contract).
            //   • Skip calling the step submit — MessageAndReminder.onSubmit only
            //     dispatches updateCompleteStatus(true) + handleNext, neither of
            //     which the V2 flow needs once updateLeadDetail owns persistence.
            //     'Booking Details' is the literal `isAppointmentBooked` value
            //     V1 sets via followUpAppointmentHandler — V2 has no equivalent
            //     dispatcher, so we pass the string directly to match V1's
            //     OverView.jsx:894 contract.
            if (bookFlowStep === 'MESSAGE_REMINDER') {
                const canFinishCurrentPatient = () => {
                    const liveState = store.getState();
                    return canonicalContextReady
                        && bookingDiagnosticScopeRef.current === bookingDiagnosticScopeKey
                        && bookingDiagnosticAttemptRef.current === bookingDiagnosticAttemptGeneration
                        && hasCanonicalPatientContext(leadDetail, practiceId, liveState.lead?.followLead, liveState.practiceTreatmentList?.practiceId)
                        && hasCanonicalPatientContext(leadDetail, practiceId, liveState.lead?.leadDetail, liveState.practiceTreatmentList?.practiceId);
                };
                if (!canFinishCurrentPatient()) return;
                dispatch(updateSteps(null));
                // ── CRITICAL FIX (2026-06-08, lead 166703) ──────────────────────
                // Persist any PersonalDetails edits captured when the user left
                // Step 2 — see personalDetailsSnapshotRef declaration for full
                // RCA. updatePersonalDetail PATCHes /api/v1/practices/{p}/
                // leads/{id}/, then we chain updateLeadDetail (which fires the
                // appointment POST) from its success callback so the
                // appointment is not booked until the personal-details edit is
                // durable. When there are no snapshot edits to persist (user
                // skipped Step 2 entirely or wizard mounted without a leadId),
                // we fall through to the original appointment-only branch —
                // same behaviour as before this fix.
                const snapshot = personalDetailsSnapshotRef.current;
                const leadIdForPatch = leadDetail?.id;
                if (snapshot && leadIdForPatch) {
                    const fireAppointment = () => {
                        if (!canFinishCurrentPatient()) return;
                        // Clear snapshot so a subsequent Book in the same
                        // session (different lead) starts clean.
                        personalDetailsSnapshotRef.current = null;
                        dispatch(updateLeadDetail('Booking Details', () => processBookingPayment(() => handleClose('finish'))));
                    };
                    // type 'leadDataGrid' just toasts on success — does not
                    // navigate away or close the modal — so fireAppointment
                    // runs as the success callback as expected.
                    dispatch(updatePersonalDetail(
                        { ...snapshot, id: leadIdForPatch },
                        practiceId,
                        fireAppointment,
                        'leadDataGrid',
                    ));
                    return;
                }
                dispatch(updateLeadDetail('Booking Details', () => processBookingPayment(() => handleClose('finish'))));
                return;
            }
            // Intermediate steps: pass 'next' so V1 step components skip their
            // status==='finish' branch (which dispatches updateCompleteStatus(true)
            // and would close the modal prematurely via markAsComplete listeners).
            //
            // CRITICAL FIX (2026-06-08, lead 166703): when leaving PERSONAL_DETAILS,
            // snapshot the form values BEFORE submit so we can re-PATCH at
            // MESSAGE_REMINDER as a belt-and-suspenders against
            // PersonalDetails.onSubmit's PATCH being silently dropped across
            // step transitions (RHF re-init / leadDetail wipe race / unmount).
            if (bookFlowStep === 'PERSONAL_DETAILS') {
                try {
                    const raw = bookingRef.current?.getValues?.();
                    if (raw) {
                        // Mirror V1 PersonalDetails.onSubmit payload shape
                        // (PersonalDetails.jsx:105-184) so the BE sees the same
                        // request body either path takes.
                        const detail = {
                            salutation: (raw.title && raw.title.toUpperCase()) || '',
                            first_name: raw.firstName || '',
                            last_name: raw.lastName || '',
                            dob: raw.dob ? moment(raw.dob, 'DD/MM/YYYY').format('DD-MM-YYYY') : null,
                            is_registered_patient: raw.isRegister,
                            lead_contact: {
                                email: raw.email || '',
                                phone: raw.phoneNumber || '',
                                ...(raw.notes ? { note: raw.notes } : {}),
                                ...(raw.preferredContactMethod
                                    ? { preferred_contact_method: raw.preferredContactMethod.toUpperCase() }
                                    : {}),
                            },
                            lead_address: {
                                address_line_1: raw.address_line_1 || '',
                                address_line_2: raw.address_line_2 || '',
                                postcode: raw.postcode || '',
                                city: raw.city || '',
                                county: raw.county || '',
                            },
                            lead_source: {
                                ...(raw.type ? { type: raw.type } : {}),
                                ...(raw.referral ? { referral: raw.referral } : {}),
                                ...(raw.affiliate_user
                                    ? { affiliate_user: raw.affiliate_user, type: 'CLINICIAN' }
                                    : {}),
                            },
                            ...(raw.sex ? { sex: raw.sex } : {}),
                            ...(raw.nhs_number ? { nhs_number: raw.nhs_number } : {}),
                            ...(raw.emergencyContactName
                                ? { emergency_contact_name: raw.emergencyContactName }
                                : {}),
                            ...(raw.emergencyContactPhone
                                ? { emergency_contact_phone: raw.emergencyContactPhone }
                                : {}),
                        };
                        personalDetailsSnapshotRef.current = detail;
                    }
                } catch (e) {
                    // getValues should never throw; if it does we still proceed
                    // — the V1 submit path runs below as the primary attempt.
                    // Snapshot stays null and MESSAGE_REMINDER will skip the
                    // re-PATCH (same as pre-fix behaviour).
                }
            }
            await bookingRef.current?.submit?.('next');
            return;
        }

        // H-3: delegate to Archive's imperative submit
        if (activeAction?.key === 'ARCHIVE') {
            archiveRef.current?.submit?.('finish');
            return;
        }

        // Active Tasks is an inline-save surface: rename/reschedule/pause/resume
        // already persist and refresh at the save point. Closing the popup should
        // not trigger NewLead's heavy table refetch; booking/archive keep their
        // explicit reload paths above.
        if (activeAction?.key === 'TASKS') {
            handleClose('finish', { skipTableRefresh: true });
            return;
        }

        // H-4: LEAD_NEXT_ACTION — ScheduleTaskReminder handles its own POST
        // to /tasks/ inline (user clicks "Save task" inside the component).
        // No batch commit — each card action persists immediately (Raj decision 2026-06-06).
        if (handleReloadLeads) handleReloadLeads();
        handleClose('finish');
    }, [
        activeTab, pmsScope.ready, pmsEnabledGlobal, dentallyVisible, dentallyCanBook, useDentallyFlow,
        bookFlowStep, bookingPatient, canonicalContextReady, handleReloadLeads, handleClose,
        bookingDiagnosticAttemptGeneration, bookingDiagnosticScopeKey,
        leadDetail?.id, practiceId,
    ]);

    // ── Lead header — shared AttendedFamilyHero component ───────────────────────
    // V2-specific divergence from CAA:
    //   • statusLabel={status} (dynamic), statusColor="#078DEE" (blue), no statusIcon
    //   • no onEdit / no onAssignPractitioner (V2 leads are pre-consultation)
    //   • extraChips: grey italic "V2 checklist" chip
    //   • children: preferred-contact row (V2-only; no CAA equivalent)
    const hasPreferredContact = preferredMethod && preferredMethod !== 'NO_PREFERENCE';

    // R-7: resolve price using the shared util — mirrors CAA.jsx:486
    // selectedTreatmentData is not a V2 prop (V2 leads have a single treatment), so we
    // pass it as undefined and use filterStatus:'NEW' to match V2 lead_treatments.
    const heroPrice = resolveLeadTreatmentPrice({ followLead, filterStatus: status });

    // ── Resilience Case 3: multi-treatment picker chip strip ────────────────────
    // When 2+ treatments share the same status as the currently-active treatment,
    // expose a small chip strip so the user can swap which one is the hero card.
    // Only renders when the disambiguation actually matters (>=2 same-status entries).
    const sameStatusTreatments = allLeadTreatments
        .map((t, idx) => ({ t, idx }))
        .filter(({ t }) => (t?.status || 'NEW') === status);
    const showTreatmentPicker = sameStatusTreatments.length >= 2;

    const headerNode = (
        <>
            {/* Instruction headline — mirrors OverView.jsx:1728 enquiry card headline:
                "{first_name} enquired about {treatment}".
                Fallback: "Contact lead about {treatment}" when first_name missing.
                Resilience Case 2: hasNoTreatments branch shows "No treatment on
                file" with a CTA hint instead of the broken "enquired about a
                treatment" line that lead_treatments[0] = undefined produces. */}
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#6e6e73', mb: 0.75, letterSpacing: '-0.005em' }}>
                {(() => {
                    if (hasNoTreatments) {
                        return <>No treatment on file — add a treatment from the lead detail page to enable follow-up actions</>;
                    }
                    const treatmentLabel = treatment !== '—' ? treatment : 'a treatment';
                    if (leadDetail?.first_name) {
                        return <><span style={{ textTransform: 'capitalize' }}>{leadDetail.first_name}</span> enquired about {treatmentLabel}</>;
                    }
                    return `Contact lead about ${treatmentLabel}`;
                })()}
            </Typography>

            {/* Resilience Case 3: multi-treatment picker. Only shown when 2+ treatments
                share the current status. Clicking a chip swaps activeTreatmentIdx so the
                hero card's treatment / price / id reflect the chosen treatment. */}
            {showTreatmentPicker && (
                <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ flexWrap: 'wrap', mb: 0.75 }}
                    data-testid="v2-treatment-picker"
                >
                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#6e6e73', alignSelf: 'center', mr: 0.5 }}>
                        Treatment:
                    </Typography>
                    {sameStatusTreatments.map(({ t, idx }) => {
                        // Status colour so Booked/Started/etc. read at a glance; duplicate
                        // names (a fresh round of the same treatment) get the created date
                        // so the two rows are distinguishable. Selection is always by idx/id.
                        const ui = getLeadStatusUi(t?.status);
                        const isDupName = sameStatusTreatments.filter(({ t: other }) => (other?.treatment || '') === (t?.treatment || '')).length > 1;
                        const createdDay = String(t?.created_at || '').slice(0, 10); // DD-MM-YYYY
                        const label = isDupName && createdDay ? `${t?.treatment || '—'} · ${createdDay}` : (t?.treatment || '—');
                        const isSelected = idx === activeTreatmentIdx;
                        return (
                            <Chip
                                key={t?.id ?? idx}
                                size="small"
                                label={label}
                                onClick={() => setActiveTreatmentIdx(idx)}
                                sx={{
                                    height: 22, fontSize: '11px', fontWeight: 600,
                                    cursor: 'pointer',
                                    bgcolor: alpha(ui.color, isSelected ? 0.16 : 0.08),
                                    color: ui.color,
                                    border: isSelected ? `1px solid ${ui.color}` : '1px solid transparent',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        );
                    })}
                </Stack>
            )}

            {/* Duplicate banner — mirrors OpenContactLeadPopup.jsx:684 SimilarLeadWarningPopup */}
            <SimilarLeadWarningPopup type="DialogPopup" dialogClose={() => {}} />

            <AttendedFamilyHero
                followLead={followLead}
                leadId={currentLeadId}
                leadName={leadName}
                treatment={treatment}
                treatmentDateTime={getV2TreatmentDateTime() || null}
                treatmentAssignees={getV2TreatmentAssignees() || []}
                assigneeSelected={assigneeSelected}
                setAssigneeSelected={setAssigneeSelected}
                /* Inline assignee toggle picker (same AssignedToLead UI as the checklist):
                   update local state immediately, then persist the lead's assignees via
                   PATCH /leads/{id}/ ({ assignees: [practice_user_id…] }) — the exact payload
                   the existing AddAssignTeamMember dialog sends. */
                onAssigneePersist={(next) => {
                    setAssigneeSelected(next);
                    dispatch(updateAssignToUser({ assignees: (next || []).map((a) => a.id) }, currentLeadId, practiceId, () => {}));
                }}
                price={heroPrice}
                hidePriceLabel
                statusLabel={status}
                statusColor="#078DEE"
                dbgSx={dbgSx}
                DbgTag={DbgTag}
                extraChips={heroFollowUpLabel ? (
                    /* Follow-up stage chip — mirrors OpenContactLeadPopup.jsx:603-641 JOURNEY_STAGES.
                       Only shown when contacted at least once (count >= 1). Lead ID + "V2 checklist"
                       badge intentionally removed — both visible in the debug overlay and were
                       cluttering the hero card during patient calls. */
                    <Chip
                        size="small"
                        icon={<Iconify icon="mdi:repeat" width={11} height={11} sx={{ color: '#FF5630 !important', ml: '5px !important' }} />}
                        label={heroFollowUpLabel}
                        sx={{
                            height: 22, fontSize: '11px', fontWeight: 600,
                            bgcolor: 'rgba(255,86,48,0.10)', color: '#FF5630',
                            border: 'none',
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                ) : null}
                // Collapse the hero to a one-row variant when the user is in the
                // Book appointment outcome — gives the multi-step booking wizard
                // more vertical screen space. Derived from activeTab via
                // V2_ACTIONS lookup so the gate is keyed on BOOK_APPOINTMENT, not
                // a fragile literal label string.
                compact={V2_ACTIONS.find((a) => a.label === activeTab)?.key === 'BOOK_APPOINTMENT'}
                statusRowExtras={(heroSourceType || heroReferral) ? (
                    /* Source / referral chips rendered inline with the lead-status chip
                       (per Raj 2026-06-08) — gives the front desk an at-a-glance read of
                       where the lead came from without scanning the body. */
                    <>
                        {heroSourceType && (
                            <Box sx={{ ...pillBadge('rgba(139,92,246,0.08)', '#8b5cf6'), fontSize: '11px', py: 0.1, height: 22 }}>
                                <Iconify icon="mdi:web" width={12} height={12} />
                                {heroSourceType}
                            </Box>
                        )}
                        {heroReferral && (() => {
                            const refEntry = referralSchema?.find?.((e) => e.value === heroReferral);
                            return (
                                <Tooltip
                                    title={`Resource referral: ${refEntry?.label || heroReferral}`}
                                    placement="bottom"
                                    TransitionComponent={Zoom}
                                    TransitionProps={{ timeout: 300 }}
                                >
                                    <Box sx={{ ...pillBadge('rgba(145,158,171,0.08)', '#637381'), fontSize: '11px', py: 0.1, height: 22, cursor: 'default' }}>
                                        {refEntry?.icon && <Iconify icon={refEntry.icon} width={12} height={12} />}
                                        {refEntry?.label || heroReferral}
                                    </Box>
                                </Tooltip>
                            );
                        })()}
                    </>
                ) : null}
                priceBelowNode={(heroPhone || heroEmail) ? (
                    /* Right-column contact stack (per Raj 2026-06-08 — Option 3):
                       PRICE on top, then phone (large/bold for dial-time legibility),
                       then email (small/muted). Everything right-aligned in one column
                       so the eye reads top-to-bottom without jumping between sides.
                       NOTE: AttendedFamilyHero's priceBelowNode wrapper already adds
                       mt:0.5, so this inner Stack carries no top margin of its own.

                       Resilience Case 4: when phone OR email is null, the row falls
                       through to a tooltipped placeholder ("No phone on file" /
                       "No email on file") instead of rendering a click-through
                       tel:/mailto: anchor that opens to nothing. */
                    <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                        {/* Email line intentionally removed from the header (Raj 2026-06-13):
                            it sat under the phone but added little value at dial-time — email
                            is still reachable via the Email action card. Phone only here. */}
                        {heroPhone ? (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.75}
                                component="a"
                                href={`tel:${heroPhone}`}
                                sx={{ textDecoration: 'none', color: 'inherit', '&:hover .phone-num': { textDecoration: 'underline' } }}
                            >
                                <Iconify icon="solar:phone-bold" width={20} sx={{ color: '#078DEE', flexShrink: 0 }} />
                                <Typography className="phone-num" sx={{ fontSize: '22px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                                    {heroPhone}
                                </Typography>
                            </Stack>
                        ) : (
                            <Tooltip title="No phone number on file" placement="left">
                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ opacity: 0.45, cursor: 'not-allowed' }}>
                                    <Iconify icon="solar:phone-bold" width={20} sx={{ color: '#919EAB', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '13px', color: '#919EAB', fontStyle: 'italic' }}>
                                        No phone on file
                                    </Typography>
                                </Stack>
                            </Tooltip>
                        )}
                    </Stack>
                ) : (
                    /* Resilience Case 4: BOTH phone + email missing. Surface the gap
                       explicitly instead of rendering an empty right column (which
                       masquerades as "still loading"). */
                    <Tooltip title="No contact information on file for this lead" placement="left">
                        <Stack direction="column" alignItems="flex-end" spacing={0.25} sx={{ opacity: 0.55 }}>
                            <Typography sx={{ fontSize: '12px', color: '#919EAB', fontStyle: 'italic' }}>
                                No phone or email on file
                            </Typography>
                        </Stack>
                    </Tooltip>
                )}
            >
                {/* Dense info rows inside hero — preferred contact + tags.
                    Phone + email now live in the right-column priceBelowNode stack
                    (per Raj 2026-06-08), so the left column only carries patient context. */}
                <Box sx={{ mt: 1.25 }}>

                    {/* Preferred-contact row — V2-only. Mirrors FollowUp.jsx:405-524 (read-only).
                        Gated: only shown when preferred_contact_method is set and not NO_PREFERENCE. */}
                    {hasPreferredContact && (
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                {capitalizeFirstLetter(resolvedLead.first_name || '') || 'Patient'} prefers
                            </Typography>
                            <Select
                                variant="outlined"
                                size="small"
                                value={preferredMethod}
                                inputProps={{ readOnly: true }}
                                sx={{ ...CONTACT_SELECT_SX, minWidth: '120px' }}
                            >
                                {PREFERRED_CONTACT_METHOD.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                ))}
                            </Select>
                            {preferredDays.length > 0 && (
                                <>
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px' }}>on</Typography>
                                    <Select
                                        variant="outlined"
                                        size="small"
                                        value={preferredDays[0]}
                                        inputProps={{ readOnly: true }}
                                        sx={CONTACT_SELECT_SX}
                                    >
                                        {PREFERRED_CONTACT_DAYS.map((item) => (
                                            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                        ))}
                                    </Select>
                                </>
                            )}
                            {preferredPeriods.length > 0 && (
                                <Select
                                    variant="outlined"
                                    size="small"
                                    value={preferredPeriods[0]}
                                    inputProps={{ readOnly: true }}
                                    sx={CONTACT_SELECT_SX}
                                >
                                    {CONTACT_PERIOD.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        </Stack>
                    )}

                    {/* Tags row — uses pillBadge (src/styles/LeadDetailView.js:868) matching V1.
                        Source + referral moved up into statusRowExtras (next to status chip).
                        Tags: mirrors LeadDetailViewBody.jsx:1574-1587 (t.colour fallback). */}
                    {heroTags.length > 0 && (
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                            {heroTags.slice(0, 3).map((tag) => (
                                <Box
                                    key={tag.id ?? tag.name}
                                    title={tag.name}
                                    sx={{
                                        ...pillBadge(
                                            tag.tag_colour ? `${tag.tag_colour}1F` : 'rgba(145,158,171,0.12)',
                                            tag.tag_colour || '#454F5B'
                                        ),
                                        fontSize: '12px',
                                        py: 0.25,
                                    }}
                                >
                                    {tag.name}
                                </Box>
                            ))}
                            {heroTags.length > 3 && (
                                <Typography sx={{ fontSize: '11px', color: '#6e6e73' }}>+{heroTags.length - 3}</Typography>
                            )}
                        </Stack>
                    )}
                </Box>
            </AttendedFamilyHero>

            {/* ChecklistProgressStrip removed 2026-06-07 per Raj — the "X / Y complete"
                row + icon chips above the chip strip was visual noise; users already see
                progress via the UpNextCardV2 hero + ChecklistMiniGrid below. Stats strip
                ("Due today / Later / Done" with green progress bar) inside reminderNode
                still renders — that one is intentional. Component import dropped below. */}

            {/* Enquiry message — collapsible if > ENQUIRY_TRUNCATE chars.
                Sourced from lead_notes[0].additional_notes (the initial web-form enquiry).
                Mirrors ArchiveDetailView.jsx:342-349 display pattern. */}
            {heroEnquiryText && (
                <Box sx={{ mb: 2, px: 3, py: 1.5, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                        <Iconify icon="solar:chat-square-bold" width={13} sx={{ color: '#6e6e73' }} />
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Enquiry
                        </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '13px', color: '#3d3d3f', lineHeight: 1.55, letterSpacing: '-0.005em' }}>
                        {heroEnquiryText.length <= ENQUIRY_TRUNCATE || enquiryExpanded
                            ? heroEnquiryText
                            : `${heroEnquiryText.slice(0, ENQUIRY_TRUNCATE)}…`}
                    </Typography>
                    {heroEnquiryText.length > ENQUIRY_TRUNCATE && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setEnquiryExpanded((v) => !v)}
                            sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: '12px', color: '#078DEE', textTransform: 'none', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                        >
                            {enquiryExpanded ? 'Show less' : 'Read more'}
                        </Button>
                    )}
                </Box>
            )}
        </>
    );

    // ── reminderNode — delegates to ScheduleReminderTaskSection ──────────────────
    // All checklist UI (UpNextCardV2 hero, ActionPanel, stats strip, pause/resume,
    // reschedule banner, RecentlyCompletedCard ack, mini-grid, custom reminder toggle)
    // now lives in ScheduleReminderTaskSection. Keyed by currentLeadId so state resets
    // on lead change without needing explicit resets in handleClose.
    const reminderNode = (
        <ScheduleReminderTaskSection
            key={currentLeadId}
            practiceId={practiceId}
            leadId={currentLeadId}
            leadDetail={followLead || leadDetail}
            leadTreatmentId={treatmentId}
            onOpenSidebarTab={handleOpenSidebarTab}
            onArmWhatsappTask={armWhatsappTask}
            onReloadLeads={handleReloadLeads}
            onClose={handleClose}
            checklist={checklist}
            dbgSx={dbgSx}
            DbgTag={DbgTag}
        />
    );

    // ── Back-to-checklist button shown inside OUTCOME box ─────────────────────
    // Not rendered on Book Appointment (2026-08-17, Raj: "we do not need this at
    // all"). The What's-Next picker directly above already offers "Active Tasks",
    // which navigates back to the checklist — this was a second, quieter control
    // for the same destination. Other outcomes (Archive, Tasks) keep it.
    const hideOutcomeBackToChecklist = V2_ACTIONS.find((a) => a.label === activeTab)?.key === 'BOOK_APPOINTMENT';
    const outcomeBackToChecklist = activeTab && !hideOutcomeBackToChecklist ? (
        <Box sx={{ mb: 2 }}>
            <Button
                size="small"
                onClick={() => { setActiveTab(V2_TASKS_ACTION_LABEL); setFlowStep('CONTENT'); }}
                sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '12px', fontWeight: 500, pl: 0, '&:hover': { bgcolor: 'transparent', color: '#1d1d1f' } }}
            >
                Back to checklist
            </Button>
        </Box>
    ) : null;

    // ── Action tabs — 4 V2 outcomes ───────────────────────────────────────────
    // `value` is consumed by the shell's gates (AttendedPopupShell:195/203 hardcode
    // the literal 'Reminder & Follow-up' for the reminderNode swap). `label` is the
    // user-facing chip text and falls back to `value` when omitted (shell handles).
    const tabs = V2_ACTIONS.map((action) => ({
        value: action.label,
        label: action.displayLabel || action.label,
        icon: makeIconNode(action.icon),
        component: (
            <OutcomePanel
                key={action.key}
                action={action}
                items={items}
                onMarkComplete={handleOutcomeMarkComplete}
                leadId={currentLeadId}
                leadDetail={leadDetail}
                activeTreatmentId={treatmentId}
                practiceId={practiceId}
                pmsScope={pmsScope}
                pmsEnabledGlobal={pmsEnabledGlobal}
                dentallyCanBook={dentallyCanBook}
                dentallyScopePending={dentallyScopePending}
                pmsSlot={pmsSlot}
                setPmsSlot={setPmsSlot}
                pmsPractitioner={pmsPractitioner}
                setPmsPractitioner={setPmsPractitioner}
                pmsDate={pmsDate}
                setPmsDate={setPmsDate}
                pmsBookingStep={pmsBookingStep}
                setPmsBookingStep={setPmsBookingStep}
                pmsConfirmStepState={pmsConfirmStepState}
                setPmsConfirmStepState={setPmsConfirmStepState}
                dentallySlot={dentallySlot}
                setDentallySlot={setDentallySlot}
                selectedDentallyTreatment={selectedDentallyTreatment}
                setSelectedDentallyTreatment={setSelectedDentallyTreatment}
                dentallyAssignees={dentallyAssignees}
                setDentallyAssignees={setDentallyAssignees}
                useDentallyFlow={useDentallyFlow}
                handleToggleBookingMode={handleToggleBookingMode}
                followUpConfirmStepState={followUpConfirmStepState}
                setFollowUpConfirmStepState={setFollowUpConfirmStepState}
                dentallyBookingStep={dentallyBookingStep}
                setDentallyBookingStep={setDentallyBookingStep}
                dentallyPatientData={dentallyPatientData}
                setDentallyPatientData={setDentallyPatientData}
                onBookingDiagnosticChange={handleBookingDiagnosticChange}
                bookFlowStep={bookFlowStep}
                advanceBookFlow={advanceBookFlow}
                bookingRef={bookingRef}
                archiveRef={archiveRef}
                onClose={handleClose}
                handleReloadLeads={handleReloadLeads}
                onExplicitArchiveSubmit={(payload) => dispatch(beginArchiveLead({
                    leadId: currentLeadId,
                    practiceId,
                    leadTreatmentId: treatmentId,
                    reason: payload?.data?.archiveReason || payload?.notes?.note || payload?.reason || '',
                    note: payload?.data?.archiveNotes || payload?.archiveData?.note || payload?.notes?.additional_notes || payload?.note || '',
                    isOpenForCommunication: payload?.openCommunication?.is_open_for_communication ?? payload?.isOpenForCommunication,
                    source: 'lead-detail:followup-v2-drawer',
                    handleClose,
                }))}
                onBookStepValidityChange={setBookStepValid}
                activeTaskCount={activeTaskCount}
                activeTaskIds={activeTaskIds}
            />
        ),
    }));

    // 2026-06-07 bundle: per-(activeTab, bookFlowStep) footer affordances.
    //
    // Label rules (Raj 2026-06-07; TASKS relabelled 2026-10-13):
    //   TASKS                                                 → 'Close'
    //   ARCHIVE                                               → 'Archive lead'
    //   BOOK_APPOINTMENT, bookFlowStep === MESSAGE_REMINDER   → 'Finish booking'
    //   BOOK_APPOINTMENT, any earlier step                    → 'Next'
    //   anything else (fallback)                              → 'Finish'
    const finishLabel = useMemo(() => {
        const activeKey = V2_ACTIONS.find((a) => a.label === activeTab)?.key;
        // 2026-10-13 — was 'Done'. On the Tasks tab this button only closes the
        // modal (handleFinish → handleClose); it never completed anything. Reps
        // read 'Done' as "mark this task done", sent a WhatsApp, pressed it, and
        // the lead stayed in the tasks-due list. See
        // RCA_2026-10-13_whatsapp_step_never_completes.md (finding F-A).
        if (activeKey === 'TASKS') return 'Close';
        if (activeKey === 'ARCHIVE') return 'Archive lead';
        if (activeKey === 'BOOK_APPOINTMENT') {
            if (bookFlowStep === 'MESSAGE_REMINDER') return 'Finish booking';
            return 'Next';
        }
        return 'Finish';
    }, [activeTab, bookFlowStep]);

    // Show Back button only inside the Book wizard, on any step AFTER the first.
    const showBack = useMemo(() => {
        const activeKey = V2_ACTIONS.find((a) => a.label === activeTab)?.key;
        return activeKey === 'BOOK_APPOINTMENT' && bookFlowStep !== 'BOOKING_DETAILS';
    }, [activeTab, bookFlowStep]);

    // 2026-06-07 bundle: disable Finish/Next when the active step says it's invalid.
    // Participating steps emit onValidityChange → bookStepValid:
    //   • BOOKING_DETAILS  (V2BookingDetailsStep — RHF formState.isValid)
    //   • PAYMENT_OPTIONS  (PaymentOption — #91, gates Next until amount > 0)
    // All other paths (TASKS, ARCHIVE, BOOK_APPOINTMENT's remaining V1 steps) stay
    // enabled so existing behavior is unchanged. TODO #64b-e: extend gating to each
    // V2-native step as it lands.
    const finishDisabled = useMemo(() => {
        const activeKey = V2_ACTIONS.find((a) => a.label === activeTab)?.key;
        if (
            activeKey === 'BOOK_APPOINTMENT'
            && dentallyCanBook
            && useDentallyFlow
            && dentallyBookingStep === 'PATIENT_DETAILS'
        ) {
            return Boolean(followUpConfirmStepState.buttonDisabled);
        }
        if (activeKey === 'BOOK_APPOINTMENT' && bookFlowStep === 'MESSAGE_REMINDER'
            && !(pmsEnabledGlobal && !dentallyVisible) && !(dentallyCanBook && useDentallyFlow) && !canonicalContextReady) return true;
        if (activeKey === 'BOOK_APPOINTMENT' && bookFlowStep === 'BOOKING_DETAILS'
            && !(pmsEnabledGlobal && !dentallyVisible) && !(dentallyCanBook && useDentallyFlow) && !bookingPatient) return true;
        if (activeKey === 'BOOK_APPOINTMENT' && (bookFlowStep === 'BOOKING_DETAILS' || bookFlowStep === 'PAYMENT_OPTIONS')) {
            return !bookStepValid;
        }
        return false;
    }, [
        activeTab,
        bookFlowStep,
        bookingPatient,
        canonicalContextReady,
        pmsEnabledGlobal,
        dentallyVisible,
        bookStepValid,
        dentallyBookingStep,
        dentallyCanBook,
        followUpConfirmStepState.buttonDisabled,
        useDentallyFlow,
    ]);

    // ── Sidebar tabs — mirrors OpenConsultationPopup.jsx:135-255 ─────────────
    // 5 always-shown tabs + 1 superadmin-only Debug tab.
    // NOTE: No "Attended Checklist" tab — irrelevant for NEW leads (V2 flow).
    const sidebarTabs = [
        {
            icon: 'grommet-icons:overview',
            label: 'Overview',
            value: 'overview',
            component: (
                <OverviewTab
                    treatmentTabData={treatmentData || []}
                    leadId={currentLeadId}
                    setExpandLayout={() => {}}
                    handleChange={handleSidebarTabChange}
                    expandLayout={false}
                    leadReminder={leadFlowReminder}
                    user={followLead || leadDetail}
                    LeadNotes={notesLength}
                    showDetailModes
                />
            ),
            width: 24,
            height: 24,
        },
        {
            icon: 'solar:chat-round-dots-bold',
            label: 'Notes',
            value: 'notes',
            component: (
                <NotesTab
                    LeadNotes={notesLength}
                    isForTab
                    userDetails={followLead || leadDetail}
                    handleChange={handleSidebarTabChange}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'solar:phone-calling-rounded-bold',
            label: 'Calls',
            value: 'calls',
            component: (
                <CallLogs leadDetail={followLead || leadDetail} />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'mdi:email-outline',
            label: 'Email',
            value: 'email',
            component: (
                <IosEmailComposerBody
                    leadId={currentLeadId}
                    practiceId={practiceId}
                    leadDetail={followLead || leadDetail}
                    templateId={sidebarEmailTemplateId}
                    compact
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'ic:baseline-whatsapp',
            label: 'WhatsApp',
            value: 'whatsapp',
            component: (
                <FollowUpWhatsappChat
                    user={followLead || leadDetail}
                    followLead={followLead}
                    practiceId={practiceId}
                    onMessageConfirmed={handleWhatsappConfirmedSend}
                    templateId={sidebarWhatsappTemplateId}
                    prefillWaTemplateId={sidebarWhatsappTemplateWaId}
                    prefillWaTemplateName={sidebarWhatsappTemplateName}
                    prefillWaItemId={sidebarWhatsappItemId}
                />
            ),
            width: 20,
            height: 20,
        },
        ...(isSuperuser ? [{
            icon: 'solar:bug-bold-duotone',
            label: 'Debug',
            value: 'debug',
            component: (
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '0.02em', textTransform: 'uppercase', fontWeight: 600, mb: 1.5 }}>
                        V2 Visibility (superadmin)
                    </Typography>
                    <Box sx={{
                        fontFamily: 'monospace', fontSize: '11px', color: '#1d1d1f', bgcolor: '#F5F5F7',
                        p: 1.5, borderRadius: '8px', lineHeight: 1.7,
                        // Must NOT influence panel width — all content must wrap/truncate
                        // within the fixed 500px sidebar. Without these, long keys/values
                        // (e.g. sampleTask.keys list) can push the monospace box wider and
                        // cause a layout reflow when rawGroups fills in after initial load.
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        minWidth: 0,
                        overflow: 'hidden',
                    }}>
                        <div>practiceId: {String(practiceId)}</div>
                        <div>leadId: {String(currentLeadId ?? '')}</div>
                        <div>ready: {String(!loading)}</div>
                        <div>leadStatus: {String(status)}</div>
                        <div>featureAccess: {String(hasFeatureAccess('tasks-v2', practiceId))}</div>
                        <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.10)', my: 1 }} />
                        <div>groupsCount: {String(debugMetrics.groupsCount)}</div>
                        <div>templatesCount: {String(debugMetrics.templatesCount)}</div>
                        <div>attendedTemplatesCount: {String(debugMetrics.attendedTemplatesCount)}</div>
                        <div>v2TemplatesCount: {String(debugMetrics.v2TemplatesCount)}</div>
                        <div>matchingGroupsCount: {String(debugMetrics.matchingGroupsCount)}</div>
                        <div>groupsWithItemsCount: {String(debugMetrics.groupsWithItemsCount)}</div>
                        <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.10)', my: 1 }} />
                        <div>immediateMode: true (queue removed 2026-06-06)</div>
                        {debugMetrics.sampleTask && (
                            <>
                                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.10)', my: 1 }} />
                                <div>sampleTask.id: {String(debugMetrics.sampleTask.id)}</div>
                                <div>sampleTask.title: {String(debugMetrics.sampleTask.title)}</div>
                                <div>sampleTask.keys: [{(debugMetrics.sampleTask.keys || []).join(', ')}]</div>
                                <div>sampleTask.checklist_items.length: {String(debugMetrics.sampleTask.checklist_items_length)}</div>
                            </>
                        )}
                    </Box>
                </Box>
            ),
            width: 18,
            height: 18,
        }] : []),
    ];

    // ── Resilience Case 1 + 5: tasks-v2 permission fallback render ──────────────
    // If the user's role doesn't have tasks-v2 ('hidden'), render a friendly empty
    // state inside the existing fullscreen shell rather than the broken checklist
    // body. Same code path covers both Case 1 (Practitioner / TCO opens modal) and
    // Case 5 (practice has is_followup_v2_enabled but user lacks tasks-v2).
    if (isTasksV2Hidden) {
        return (
            <AttendedFullscreenWrapper
                open={open}
                onClose={handleClose}
                type="lead"
                headerButtons
                headerTitle={`Contact follow-up — ${leadName || 'lead'}`}
                popupName="Contact follow-up"
                followLead={canonicalContextReady ? followLead : null}
                sidebarTabs={[]}
                sidebarTab="overview"
                onTabChange={() => {}}
            >
                <Box
                    data-testid="followupv2-no-access"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 1.5,
                        py: 8,
                        px: 4,
                        height: '100%',
                    }}
                >
                    <Iconify icon="solar:lock-keyhole-minimalistic-bold-duotone" width={56} sx={{ color: '#919EAB' }} />
                    <Typography variant="h6" sx={{ color: '#1d1d1f', fontWeight: 600 }}>
                        You don&apos;t have access to Follow-Up V2
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6e6e73', maxWidth: 420 }}>
                        Your role doesn&apos;t include the Tasks V2 permission required to use this
                        view. Ask a practice admin to update your role permissions in Settings
                        &rarr; Feature flags, or close this dialog to return to the dashboard.
                    </Typography>
                    <Button
                        variant="text"
                        href="mailto:hello@dentalsem.co.uk?subject=Tasks%20V2%20access%20request"
                        sx={{ mt: 1, color: '#078DEE', textTransform: 'none', fontWeight: 600 }}
                    >
                        Contact admin
                    </Button>
                    <Button
                        onClick={() => handleClose('close')}
                        variant="outlined"
                        sx={{ mt: 0.5, textTransform: 'none' }}
                    >
                        Close
                    </Button>
                </Box>
            </AttendedFullscreenWrapper>
        );
    }

    // Keep fetch hooks above running, but never mount patient actions or
    // composers with another patient's canonical data or booking practice.
    if (!canonicalContextReady) {
        return (
            <AttendedFullscreenWrapper
                open={open}
                onClose={handleClose}
                type="lead"
                headerButtons
                headerTitle="Contact follow-up"
                popupName="Contact follow-up"
                followLead={null}
                sidebarTabs={[]}
                sidebarTab="overview"
                onTabChange={() => {}}
            >
                <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: '100%', p: 4 }}>
                    <Typography>Loading lead details…</Typography>
                    <Button variant="outlined" onClick={() => handleClose('close')}>Close</Button>
                </Stack>
            </AttendedFullscreenWrapper>
        );
    }

    return (
        <>
        <AttendedFullscreenWrapper
            open={open}
            onClose={handleClose}
            type="lead"
            headerButtons
            // 2026-06-07 bug-fix #5: sentence case (matches body labels "Contact follow-up").
            // Em-dash separator already in place.
            headerTitle={`Contact follow-up — ${leadName}`}
            popupName="Contact follow-up"
            // F-1: FollowUpBadge props — mirrors OpenAttendedPopup.jsx:225-234.
            // followLead from Redux, appointmentFilter targets BOOKED appointments
            // (V2 leads have upcoming BOOKED appts, not ATTENDED).
            // checklistDone/Total derived from items array (no second hook call needed).
            followLead={followLead}
            appointmentFilter={v2AppointmentFilter}
            checklistDone={badgeChecklistDone}
            checklistTotal={badgeChecklistTotal}
            sidebarTabs={sidebarTabs}
            sidebarTab={sidebarTab}
            onTabChange={handleSidebarTabChange}
            sidebarPulseKey={sidebarPulse}
            pulseCssName="followupV2Pulse"
        >
            <AttendedPopupShell
                practiceId={practiceId}
                flowStep={flowStep}
                activeTab={activeTab}
                onActiveTabChange={handleTabChange}
                tabs={tabs}
                hiddenTabs={[]}
                // Pin 'Active tasks' chip LEFT and Book/Archive/Can't Reach RIGHT.
                // Attended doesn't pass this prop → its chip row stays unchanged
                // (all chips continuous, left-aligned). Added 2026-06-07.
                splitGroup
                // Wider chips so 'Book appointment' / 'Active tasks' aren't ellipsised
                // by the shell's whiteSpace:nowrap + textOverflow:ellipsis rules
                // (AttendedPopupShell.jsx:155 + 167). Attended doesn't pass chipMinWidth
                // → defaults to 62 (existing behavior) → its chip dimensions unchanged.
                chipMinWidth={140}
                // Show labels on UNSELECTED chips too (OUTCOME_CHOSEN mode), so users
                // can read 'Active tasks' / 'Book appointment' / 'Archive' without
                // hovering. Attended doesn't pass this → its compact icon-only
                // IconButton row is preserved.
                alwaysShowChipLabels
                showSimilarLeadWarning={false}
                tcoNode={null}
                methods={methods}
                onFormSubmit={() => {}}
                headerNode={headerNode}
                gateNode={null}
                reminderNode={reminderNode}
                outcomeBackToChecklist={outcomeBackToChecklist}
                onFinish={handleFinish}
                finishLoading={bookingInFlight}
                // 2026-06-07 bundle: dynamic per-(activeTab, bookFlowStep) Finish copy +
                // disable until current step valid + secondary Back on multi-step Book.
                // Attended doesn't pass any of these → its footer chrome is unchanged.
                finishLabel={finishLabel}
                finishHelperText={leadNextActionFooter.helper}
                // `|| bookingInFlight` is belt-and-braces: the shell's LoadingButton
                // already disables itself while `loading`, but AttendedPopupShell has a
                // second footer button that reads `finishDisabled` alone.
                finishDisabled={finishDisabled || bookingInFlight}
                showBack={showBack}
                onBack={regressBookFlow}
                backLabel="Back"
                isFinishAvailable
                themeStretch={false}
                handleClose={handleClose}
                // Live dbgSx/DbgTag from the attDebug toggle above — mirrors
                // AttendedAppointment.jsx:130-133. When attDebug is false, dbgSx
                // returns null and DbgTag renders nothing (same visual as the old noops).
                // When attDebug is true (?attDebug=1 or dev mode), section outlines
                // + labels appear — matching the Attended debug experience exactly.
                dbgSx={dbgSx}
                DbgTag={DbgTag}
            />
        </AttendedFullscreenWrapper>

        <ContactPopupDebugBox
            popup="FollowUpV2 (V2)"
            debugSource={debugSource}
            practiceId={practiceId}
            leadId={currentLeadId}
            taskId={resolvedLead.task_id || null}
            isFollowUpV2
            // Payload provenance — which tier supplied each hero field. The
            // entry-point label alone could not explain why the same lead
            // rendered different chips from different buttons; this can.
            extra={{
                leadPayload: {
                    leadDetailKeys: Object.keys(leadDetail || {}).length,
                    hasOriginalData: !!leadDetail?.originalData,
                    followLeadMatches: followLead?.id === anchorLeadId,
                    selfFetched: !!selfFetchedLead,
                    resolvedLeadIncomplete,
                    sources: describeContactLeadSources(resolvedLead),
                },
            }}
            initialStep={{ flowStep, bookFlowStep }}
            activeOutcome={{
                activeTab,
                activeActionKey: V2_ACTIONS.find((a) => a.label === activeTab)?.key || null,
                bookFlowStep,
            }}
            bookingDiagnostic={bookingDiagnostic}
        />

        </>
    );
}

FollowUpV2.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    leadDetail: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Optional callback after a successful Finish to reload the leads table */
    handleReloadLeads: PropTypes.func,
    /** Diagnostic-only label identifying the launch site (superadmin debug box) */
    debugSource: PropTypes.string,
    leadTreatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

FollowUpV2.defaultProps = {
    open: false,
    onClose: () => {},
    leadDetail: null,
    practiceId: null,
    handleReloadLeads: null,
    leadTreatmentId: null,
};

// ── OutcomePanel ─────────────────────────────────────────────────────────────
// Content shown in the OUTCOME box when an action tab is chosen.
//
// Auto-complete actions (WhatsApp / Phone / Next Action) show the queued item.
// BOOK_APPOINTMENT (H-2/H-5): renders TreatmentAndAppointment or PMS steps.
// ARCHIVE (H-3): renders Archive reason picker (reuses V1 Archive component).
// LEAD_NEXT_ACTION (H-4): renders ScheduleTaskReminder (POST /tasks/, same as Attended).
// UNABLE_TO_CONTACT: stub — full integration in Sprint 2.

// eslint-disable-next-line react/no-multi-comp
function OutcomePanel({
    action, items, onMarkComplete,
    leadId, leadDetail, practiceId,
    // The treatment the user is acting on (picker-aware) — bookings must target this id,
    // never lead_treatments[0] (duplicate-name leads book the wrong row otherwise).
    activeTreatmentId,
    activeTaskCount, activeTaskIds,
    pmsScope, pmsEnabledGlobal, dentallyCanBook, dentallyScopePending,
    pmsSlot, setPmsSlot, pmsPractitioner, setPmsPractitioner,
    pmsDate, setPmsDate, pmsBookingStep, setPmsBookingStep,
    pmsConfirmStepState, setPmsConfirmStepState,
    dentallySlot, setDentallySlot,
    selectedDentallyTreatment, setSelectedDentallyTreatment,
    dentallyAssignees, setDentallyAssignees,
    useDentallyFlow, handleToggleBookingMode,
    followUpConfirmStepState, setFollowUpConfirmStepState,
    dentallyBookingStep, setDentallyBookingStep,
    dentallyPatientData, setDentallyPatientData,
    onBookingDiagnosticChange,
    bookFlowStep, advanceBookFlow,
    bookingRef, archiveRef,
    onClose, handleReloadLeads, onExplicitArchiveSubmit,
    // 2026-06-07 bundle: V2-native steps push their formState.isValid up here so
    // FollowUpV2 can disable the shell's Next/Finish button when the active step
    // is invalid. Only V2BookingDetailsStep emits today (#64a); add per step as
    // #64b-e land.
    onBookStepValidityChange,
}) {
    // eslint-disable-next-line camelcase
    const matchingItem = items.find((it) => it.item_type === action.key && it.completion_state === 'OPEN');
    const isAutoComplete = AUTO_COMPLETE_TYPES.has(action.key);

    // H-4: custom reminder toggle — mirrors useReminderContent.jsx:115-155.
    // Must be at the top of the component (before any conditional returns) per React hook rules.
    const [showCustomReminder, setShowCustomReminder] = useState(false);

    // Common action header — Archive gets a smaller icon to reclaim vertical space.
    const isArchiveAction = action.key === 'ARCHIVE';
    const actionHeader = (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: isArchiveAction ? 1.5 : 2 }}>
            <Box sx={{
                width: isArchiveAction ? 36 : 44,
                height: isArchiveAction ? 36 : 44,
                borderRadius: '12px',
                bgcolor: action.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1.5px solid ${action.color}33`, flexShrink: 0,
            }}>
                <Iconify icon={action.icon} width={isArchiveAction ? 18 : 22} sx={{ color: action.color }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em' }}>
                    {action.label}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.25 }}>
                    {action.sub}
                </Typography>
            </Box>
        </Stack>
    );

    // ── H-2/H-5 + Task #63: Book — full V1 parity ─────────────────────────────
    // Mirrors OverView.jsx:738-859 verbatim across three branches:
    //   1. PMS path   (pmsEnabledGlobal && !dentallyEnabledGlobal)
    //        — 2-step wizard: PmsAvailabilityWizardStep → PmsBookingConfirmStep
    //   2. Dentally path (dentallyEnabledGlobal && useDentallyFlow [and NOT pmsEnabledGlobal])
    //        — 2-step wizard with BookingModeToggle:
    //          DentallyTreatmentAvailabilityWizardStep → DentallyFollowUpConfirmWizardStep
    //   3. Default / CRM path (everything else, incl. Dentally-enabled-but-toggled-off)
    //        — single-step TreatmentAndAppointment with optional BookingModeToggle so the
    //          user can flip BACK to the Dentally flow.
    //
    // Existing shared shape (mirrors OverView):
    //   - existingLead built from leadDetail (id, first/last name, lead_contact email/phone,
    //     and Dentally-only supported patient-create fields: dob and salutation).
    //     TODO (gap #5, Task #63): V1 reads `currentTreatmentData?.treatmentData?.id` so
    //     multi-treatment leads honor the user-selected treatment. V2 currently uses
    //     `lead_treatments[0].id` — fine for single-treatment leads, drops the explicit
    //     selection on multi-treatment leads. Wire `setCurrentTreatmentDataGlobal` from
    //     a future treatment-picker UI to close this.
    //   - On success in PMS/Dentally paths: dispatch followUpSlice(leadId, practiceId, 'LeadDetail')
    //     BEFORE handleClose so the lead refreshes and the pending-booking banner appears
    //     immediately (gap #4). Also fire handleReloadLeads so the table re-renders the lead's
    //     new status (gap #6) — V1 doesn't do this explicitly because it returns the user to
    //     the table via separate navigation; V2 stays in-modal so the table must re-fetch.
    // NOTE (2026-08-17, Raj): the BOOK_APPOINTMENT branches deliberately render NO
    // actionHeader. The icon + "Book" + "Opens the booking wizard" block duplicated
    // what the What's-Next picker directly above already says ("Book appointment")
    // and cost ~60px of vertical space on every booking screen, in all three modes
    // (PMS / Dentally / CRM). Other outcomes (Archive, Tasks, …) still use it.
    if (action.key === 'BOOK_APPOINTMENT') {
        if (!pmsScope.ready) return <PmsSchedulingStatus scope={pmsScope} />;
        // Scope for this practice has not settled yet — hold a neutral step so the
        // CRM form is never painted and then replaced by the Dentally wizard (or
        // the reverse). `pending` is false the moment the answer is knowable, so
        // a practice with the feature hidden never sees this.
        if (dentallyScopePending) return <DentallyScopePendingStep />;
        // existingLead shape — mirrors OverView.jsx:766-774 for PMS branch and
        // :815-829 for Dentally (Dentally needs richer patient profile).
        const baseExistingLead = leadDetail ? {
            id: leadDetail.id,
            first_name: leadDetail.first_name,
            last_name: leadDetail.last_name,
            lead_contact: {
                phone: leadDetail.lead_contact?.phone,
                email: leadDetail.lead_contact?.email,
            },
        } : null;
        const dentallyExistingLead = leadDetail ? {
            ...baseExistingLead,
            dob: leadDetail.dob,
            salutation: leadDetail.salutation,
        } : null;
        // existingTreatmentId — the SELECTED treatment (picker-aware), never [0]:
        // with duplicate-name treatments (old CLOSED + new round) [0] booked the PMS/
        // Dentally appointment against the completed treatment. Number-cast to match V1.
        let existingTreatmentId = null;
        if (activeTreatmentId != null) existingTreatmentId = Number(activeTreatmentId);
        else if (leadDetail?.lead_treatments?.[0]?.id != null) existingTreatmentId = Number(leadDetail.lead_treatments[0].id);

        // ── Path 1 — PMS (mirrors OverView.jsx:740-785) ──────────────────────
        if (pmsEnabledGlobal && !dentallyCanBook) {
            if (pmsBookingStep === 'AVAILABILITY') {
                return (
                    <Box>
                        <PmsAvailabilityWizardStep
                            ref={bookingRef}
                            handleNext={() => setPmsBookingStep('CONFIRM')}
                            practiceId={practiceId}
                            pmsSlot={pmsSlot}
                            onSlotSelected={setPmsSlot}
                            selectedPractitioner={pmsPractitioner}
                            onPractitionerSelected={setPmsPractitioner}
                            selectedDate={pmsDate}
                            onDateSelected={setPmsDate}
                        />
                    </Box>
                );
            }
            return (
                <Box>
                    {/* Gap #3 (Task #63): pmsConfirmStepState is now tracked + spread back
                        in like V1 does (OverView.jsx:760, :782), so the confirm step's
                        loading/disabled flags surface to V2's Finish button. */}
                    <PmsBookingConfirmStep
                        ref={bookingRef}
                        pmsSlot={pmsSlot}
                        practiceId={practiceId}
                        existingLead={baseExistingLead}
                        existingTreatmentId={existingTreatmentId}
                        {...pmsConfirmStepState}
                        onClose={(success) => {
                            if (success) {
                                // Gap #4 — refresh lead so the pending banner renders.
                                // Gap #6 — refresh leads table so the row reflects new status.
                                dispatch(followUpSlice(leadDetail?.id, practiceId, 'LeadDetail'));
                                if (handleReloadLeads) handleReloadLeads();
                                onClose('finish');
                            }
                        }}
                        onStepStateChange={setPmsConfirmStepState}
                    />
                </Box>
            );
        }

        // ── Path 2 — Dentally (mirrors OverView.jsx:786-841) ─────────────────
        // V1 condition: dentallyEnabledGlobal && useDentallyFlow (and implicitly
        // NOT pmsEnabledGlobal because the PMS branch already returned above).
        if (dentallyCanBook && useDentallyFlow) {
            if (dentallyBookingStep === 'AVAILABILITY') {
                return (
                    <Box>
                        {/* BookingModeToggle — lets the user flip out of Dentally flow.
                            Mirrors OverView.jsx:792. sx drops the component's default
                            dialog inset so the "Book into" label lines up with the
                            wizard's other section labels instead of sitting 24px in. */}
                        <BookingModeToggle
                            useDentally
                            onChange={handleToggleBookingMode}
                            sx={{ px: 0, pt: 0, mb: 1.5 }}
                        />
                        <DentallyTreatmentAvailabilityWizardStep
                            ref={bookingRef}
                            handleNext={() => setDentallyBookingStep('PATIENT_DETAILS')}
                            // Inline in the V2 outcome box, not in a Dialog — drop the
                            // dialog inset so these sections are the same width as the
                            // CRM-only step's (was 815px vs 863px).
                            disableGutters
                            practiceId={practiceId}
                            dentallySlot={dentallySlot}
                            onSlotSelected={setDentallySlot}
                            selectedDentallyTreatment={selectedDentallyTreatment}
                            onTreatmentSelected={setSelectedDentallyTreatment}
                            assignees={dentallyAssignees}
                            onAssigneesChanged={setDentallyAssignees}
                        />
                    </Box>
                );
            }
            if (dentallyBookingStep === 'PATIENT_DETAILS') {
                return (
                    <Box>
                        <DentallyPatientDetailsWizardStep
                            ref={bookingRef}
                            practiceId={practiceId}
                            existingLead={dentallyExistingLead}
                            onStepStateChange={setFollowUpConfirmStepState}
                            onPatientDataCollected={(patientData) => {
                                setDentallyPatientData(patientData);
                                setDentallyBookingStep('CONFIRM');
                            }}
                        />
                    </Box>
                );
            }
            return (
                <Box>
                    {/* Gap #3 (Task #63) parity — followUpConfirmStepState spread in
                        like V1 does (OverView.jsx:809). */}
                    <DentallyFollowUpConfirmWizardStep
                        ref={bookingRef}
                        selectedSlot={dentallySlot}
                        selectedDentallyTreatment={selectedDentallyTreatment}
                        assignees={dentallyAssignees}
                        practiceId={practiceId}
                        existingLead={dentallyExistingLead}
                        patientData={dentallyPatientData}
                        requirePatientDetails
                        existingTreatmentId={existingTreatmentId}
                        onBookingDiagnosticChange={onBookingDiagnosticChange}
                        {...followUpConfirmStepState}
                        onClose={(success) => {
                            if (success) {
                                // Gap #4 + #6 — see PMS branch above.
                                dispatch(followUpSlice(leadDetail?.id, practiceId, 'LeadDetail'));
                                if (handleReloadLeads) handleReloadLeads();
                                onClose('finish');
                            }
                        }}
                        onStepStateChange={setFollowUpConfirmStepState}
                    />
                </Box>
            );
        }

        // ── Path 3 — Default / CRM (mirrors OverView.jsx:842-854 + :877-903) ─
        // Non-PMS, non-Dentally — or Dentally-enabled but user toggled to CRM mode.
        // When Dentally is enabled, BookingModeToggle is shown so the user can flip
        // BACK to the Dentally wizard. Mirrors OverView.jsx:847 exactly.
        //
        // Task #63 follow-up (2026-06-07): this branch is now a 5-step wizard
        // (Booking Details → Personal Details → Payment options → [opt] Dentally
        // Confirmation → Message review & reminders) mirroring V1's
        // OverView.jsx:877-903 step array. Each step is a forwardRef component
        // that calls handleNext(true) on successful submit; advanceBookFlow handles
        // the state transition. The Finish button calls bookingRef.current.submit('finish')
        // on whichever step is currently active — same imperative pattern V1 uses.
        //
        // The "Booking details" header is unchanged (actionHeader) — V2 doesn't render
        // V1's per-step labels ('Personal Details', 'Payment options', etc.) above each
        // step because V2's modal chrome is flatter. The wizard sequence itself matches.
        const bookFlowHandleNext = () => advanceBookFlow();
        let stepNode = null;
        if (bookFlowStep === 'BOOKING_DETAILS') {
            // #64a — V2-native step 1. Replaces V1 TreatmentAndAppointment(followUp, isOverview).
            // Same imperative ref API, same handleNext, same endpoint contract (no BE change).
            // 2026-06-07 bundle: onValidityChange wires the step's RHF formState.isValid
            // up to FollowUpV2's bookStepValid so the shell's Finish/Next button is
            // disabled until required fields are filled.
            stepNode = (
                <V2BookingDetailsStep
                    ref={bookingRef}
                    handleNext={bookFlowHandleNext}
                    onValidityChange={onBookStepValidityChange}
                />
            );
        } else if (bookFlowStep === 'PERSONAL_DETAILS') {
            // Mirror OverView.jsx:888.
            stepNode = <PersonalDetails ref={bookingRef} handleNext={bookFlowHandleNext} />;
        } else if (bookFlowStep === 'PAYMENT_OPTIONS') {
            // Mirror OverView.jsx:892. #91 — wire onValidityChange so the parent
            // Finish/Next button stays disabled until a valid payment selection
            // is in place (mirrors V2BookingDetailsStep's bookStepValid contract).
            stepNode = (
                <PaymentOption
                    ref={bookingRef}
                    handleNext={bookFlowHandleNext}
                    onValidityChange={onBookStepValidityChange}
                />
            );
        } else if (bookFlowStep === 'DENTALLY_CONFIRMATION') {
            // Mirror OverView.jsx:897 — only reached when dentallyEnabledGlobal && isDentallyBooking.
            stepNode = <DentallyPaymentConfirmation ref={bookingRef} handleNext={bookFlowHandleNext} />;
        } else if (bookFlowStep === 'MESSAGE_REMINDER') {
            // Mirror OverView.jsx:902 — type='CreatLead' for the booked-flow MessageAndReminder.
            stepNode = <MessageAndReminder ref={bookingRef} handleNext={bookFlowHandleNext} type="CreatLead" />;
        }
        return (
            <Box>
                {/* BookingModeToggle only on the FIRST step (selecting an appointment) —
                    after the user advances past Booking Details, the mode is locked in. */}
                {dentallyCanBook && bookFlowStep === 'BOOKING_DETAILS' && (
                    <BookingModeToggle
                        useDentally={false}
                        onChange={handleToggleBookingMode}
                        sx={{ px: 0, pt: 0, mb: 1.5 }}
                    />
                )}
                {stepNode}
            </Box>
        );
    }

    // ── H-3: Archive — reuse V1 Archive component (reason picker + notes) ─────
    // compact=true suppresses Archive's own duplicate header/description and
    // tightens card spacing; the actionHeader sub above already reads
    // "Archive this lead with a reason" so no second description is needed.
    if (action.key === 'ARCHIVE') {
        return (
            <Box>
                {actionHeader}
                <Archive
                    ref={archiveRef}
                    user={leadDetail}
                    handleClose={onClose}
                    handleReloadLeads={handleReloadLeads}
                    isDuplicate={false}
                    compact
                    onExplicitArchiveSubmit={onExplicitArchiveSubmit}
                />
            </Box>
        );
    }

    // ── H-4: LEAD_NEXT_ACTION = "Add reminder" chip (renamed 2026-06-07) ──────
    // Mirrors useReminderContent.jsx:113-156: an optional "matching item" callout when
    // a LEAD_NEXT_ACTION checklist item happens to be open (auto-complete-if-present),
    // followed by the same ScheduleTaskReminder toggle Attended uses. The "matching item"
    // section is a graceful bridge: if the lead's checklist already has a NextAction item
    // open, completing it here saves the user a click; if not, the user just clicks
    // "Add custom reminder" to schedule a fresh one — same as Attended.
    // ── H-4 + auto-complete: LEAD_NEXT_ACTION — checklist queue + ScheduleTaskReminder ────
    // Mirrors useReminderContent.jsx:113-155 — same ScheduleTaskReminder component,
    // same createTask dispatch, same POST /tasks/ endpoint as Attended.
    // "Add custom reminder" toggle mirrors useReminderContent.jsx:115-155 exactly:
    //   collapsed → "Add custom reminder" button (eva:plus-fill icon).
    //   expanded  → header + Remove button + ScheduleTaskReminder form.
    // ScheduleTaskReminder calls handleClose() on success, closing the modal automatically.
    if (action.key === 'LEAD_NEXT_ACTION') {
        return (
            <Box>
                {actionHeader}

                {/* Checklist auto-complete portion — immediate-persist on click */}
                {matchingItem && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: '#6e6e73', mb: 1 }}>
                            This will immediately complete the oldest open matching checklist item:
                        </Typography>
                        <TaskCardV2 item={matchingItem} variant="compact" />
                        <Button
                            variant="contained" size="small" disableElevation
                            onClick={() => onMarkComplete && onMarkComplete(matchingItem)}
                            sx={{
                                mt: 1.5, bgcolor: action.color, color: '#fff',
                                textTransform: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                                '&:hover': { bgcolor: action.color, filter: 'brightness(0.9)' },
                            }}
                        >
                            Mark complete
                        </Button>
                    </Box>
                )}

                {/* H-4: Custom reminder toggle — mirrors useReminderContent.jsx:115-155.
                    Collapsed: "Add custom reminder" button.
                    Expanded: header + Remove button + ScheduleTaskReminder. */}
                {showCustomReminder ? (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#637381' }}>Custom reminder</Typography>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setShowCustomReminder(false)}
                                startIcon={<Iconify icon="eva:close-fill" width={16} height={16} />}
                                sx={{ color: '#919EAB', textTransform: 'none', fontSize: '12px', minWidth: 0 }}
                            >
                                Remove
                            </Button>
                        </Box>
                        <ScheduleTaskReminder
                            practiceId={practiceId}
                            leadId={leadId}
                            leadDetail={leadDetail}
                            handleClose={onClose}
                            handleReloadLeads={handleReloadLeads}
                            activeTaskCount={activeTaskCount}
                            activeTaskIds={activeTaskIds}
                            leadTreatmentId={matchingItem?.leadTreatment ?? leadDetail?.lead_treatments?.[0]?.id ?? null}
                        />
                    </Box>
                ) : (
                    <Button
                        variant="text"
                        onClick={() => setShowCustomReminder(true)}
                        startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
                        sx={{ color: '#637381', fontWeight: 600, fontSize: '13px', textTransform: 'none', px: 1, py: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' } }}
                    >
                        Add custom reminder
                    </Button>
                )}
            </Box>
        );
    }

    // ── Auto-complete (WhatsApp / Phone) — immediate-persist on click ─────────
    // Raj decision 2026-06-06: fire completeChecklistItem immediately, not on Finish.
    if (isAutoComplete) {
        return (
            <Box>
                {actionHeader}
                {matchingItem && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: '#6e6e73', mb: 1 }}>
                            This will immediately complete the oldest open matching checklist item:
                        </Typography>
                        <TaskCardV2 item={matchingItem} variant="compact" />
                        <Button
                            variant="contained" size="small" disableElevation
                            onClick={() => onMarkComplete && onMarkComplete(matchingItem)}
                            sx={{
                                mt: 1.5, bgcolor: action.color, color: '#fff',
                                textTransform: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                                '&:hover': { bgcolor: action.color, filter: 'brightness(0.9)' },
                            }}
                        >
                            Mark complete
                        </Button>
                    </Box>
                )}
                {!matchingItem && (
                    <Box sx={{ p: 2, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.04)', mb: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: '#6e6e73' }}>
                            No open checklist item of this type for this lead.
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }

    // ── UNABLE_TO_CONTACT: stub (Sprint 2) ────────────────────────────────────
    return (
        <Box>
            {actionHeader}
            <Box sx={{ p: 2, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.04)', mb: 2 }}>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73' }}>
                    Unable to contact flow integration — Sprint 2
                </Typography>
            </Box>
        </Box>
    );
}

OutcomePanel.propTypes = {
    activeTreatmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    action: PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        sub: PropTypes.string.isRequired,
        icon: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        bg: PropTypes.string.isRequired,
    }).isRequired,
    items: PropTypes.array.isRequired,
    onMarkComplete: PropTypes.func.isRequired,
    leadId: PropTypes.number,
    leadDetail: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pmsScope: PropTypes.object.isRequired,
    pmsEnabledGlobal: PropTypes.bool,
    dentallyCanBook: PropTypes.bool,
    dentallyScopePending: PropTypes.bool,
    pmsSlot: PropTypes.object,
    setPmsSlot: PropTypes.func,
    pmsPractitioner: PropTypes.object,
    setPmsPractitioner: PropTypes.func,
    pmsDate: PropTypes.object,
    setPmsDate: PropTypes.func,
    pmsBookingStep: PropTypes.string,
    setPmsBookingStep: PropTypes.func,
    // Task #63 — PMS confirm-step state pass-through (gap #3) + Dentally booking flow (gaps #1+#2)
    pmsConfirmStepState: PropTypes.object,
    setPmsConfirmStepState: PropTypes.func,
    dentallySlot: PropTypes.object,
    setDentallySlot: PropTypes.func,
    selectedDentallyTreatment: PropTypes.object,
    setSelectedDentallyTreatment: PropTypes.func,
    dentallyAssignees: PropTypes.array,
    setDentallyAssignees: PropTypes.func,
    useDentallyFlow: PropTypes.bool,
    handleToggleBookingMode: PropTypes.func,
    followUpConfirmStepState: PropTypes.object,
    setFollowUpConfirmStepState: PropTypes.func,
    dentallyBookingStep: PropTypes.string,
    setDentallyBookingStep: PropTypes.func,
    dentallyPatientData: PropTypes.object,
    setDentallyPatientData: PropTypes.func,
    onBookingDiagnosticChange: PropTypes.func,
    bookFlowStep: PropTypes.string,
    advanceBookFlow: PropTypes.func,
    bookingRef: PropTypes.object,
    archiveRef: PropTypes.object,
    onClose: PropTypes.func,
    handleReloadLeads: PropTypes.func,
    onExplicitArchiveSubmit: PropTypes.func,
    /** 2026-06-07 bundle: receives true/false from V2-native step's RHF isValid. */
    onBookStepValidityChange: PropTypes.func,
    /** Count of OPEN tasks — drives "Existing tasks (N)" visibility in ScheduleTaskReminder. */
    activeTaskCount: PropTypes.number,
    /** Deduplicated OPEN task IDs — used for bulk-complete in ScheduleTaskReminder. */
    activeTaskIds: PropTypes.arrayOf(PropTypes.number),
};

OutcomePanel.defaultProps = {
    leadId: null,
    leadDetail: null,
    practiceId: null,
    pmsEnabledGlobal: false,
    dentallyCanBook: false,
    dentallyScopePending: false,
    pmsSlot: null,
    setPmsSlot: () => {},
    pmsPractitioner: null,
    setPmsPractitioner: () => {},
    pmsDate: null,
    setPmsDate: () => {},
    pmsBookingStep: 'AVAILABILITY',
    setPmsBookingStep: () => {},
    pmsConfirmStepState: {},
    setPmsConfirmStepState: () => {},
    dentallySlot: null,
    setDentallySlot: () => {},
    selectedDentallyTreatment: null,
    setSelectedDentallyTreatment: () => {},
    dentallyAssignees: [],
    setDentallyAssignees: () => {},
    useDentallyFlow: true,
    handleToggleBookingMode: () => {},
    followUpConfirmStepState: {},
    setFollowUpConfirmStepState: () => {},
    dentallyBookingStep: 'AVAILABILITY',
    dentallyPatientData: null,
    bookFlowStep: 'BOOKING_DETAILS',
    advanceBookFlow: () => {},
    setDentallyBookingStep: () => {},
    setDentallyPatientData: () => {},
    onBookingDiagnosticChange: () => {},
    bookingRef: null,
    archiveRef: null,
    onClose: () => {},
    handleReloadLeads: null,
    onExplicitArchiveSubmit: null,
    onBookStepValidityChange: () => {},
    activeTaskCount: 0,
    activeTaskIds: [],
};
